export const API_ENDPOINTS = {
  // Data Cleansing
  dataCleansing: {
    clear: '/datacleansing/clear',
    dependencies: '/datacleansing/dependencies',
  },

  // Data Integrity
  dataIntegrity: {
    categories: '/dataintegrity/categories',
    check: '/dataintegrity/check',
    repair: '/dataintegrity/repair',
  },

  costRepair: {
    preview: '/cost-repair/preview',
    start: '/cost-repair/start',
    jobs: '/cost-repair/jobs',
    job: (id: number | string) => `/cost-repair/jobs/${id}`,
    pause: (id: number | string) => `/cost-repair/jobs/${id}/pause`,
    resume: (id: number | string) => `/cost-repair/jobs/${id}/resume`,
    cancel: (id: number | string) => `/cost-repair/jobs/${id}/cancel`,
    retry: (id: number | string) => `/cost-repair/jobs/${id}/retry`,
    errors: (id: number | string) => `/cost-repair/jobs/${id}/errors`,
  },

  documentRepair: {
    preview: '/document-repair/preview',
    repair: '/document-repair/repair',
  },

  adminOperations: {
    summary: '/admin-operations/summary',
    feed: '/admin-operations/feed',
  },

  // Schema comparison (model vs database)
  schema: {
    compareModels: '/schema/compare-models',
    applyModelMissing: '/schema/apply-model-missing',
    export: '/schema/export',
    compare: '/schema/compare',
    applyMissing: '/schema/apply-missing',
  },

  // Notifications
  notifications: {
    all: '/notifications/all',
    state: '/notifications/state',
    stateReadAll: '/notifications/state/read-all',
    engine: {
      catalog: '/notification-engine/catalog',
      definitions: '/notification-engine/definitions',
      definition: (id: string) => `/notification-engine/definitions/${id}`,
      definitionActive: (id: string) => `/notification-engine/definitions/${id}/active`,
      definitionTest: (id: string) => `/notification-engine/definitions/${id}/test`,
      providers: '/notification-engine/providers',
      users: '/notification-engine/lookups/users',
      roles: '/notification-engine/lookups/roles',
      inbox: '/notification-engine/inbox',
      inboxUnread: '/notification-engine/inbox/unread-count',
      inboxRead: (id: string) => `/notification-engine/inbox/${id}/read`,
      inboxReadAll: '/notification-engine/inbox/read-all',
      inboxArchive: (id: string) => `/notification-engine/inbox/${id}/archive`,
      inboxHide: (id: string) => `/notification-engine/inbox/${id}/hide`,
      inboxUnhide: (id: string) => `/notification-engine/inbox/${id}/unhide`,
      inboxDelete: (id: string) => `/notification-engine/inbox/${id}/delete`,
      monitor: '/notification-engine/monitor',
      monitorRetry: (id: string) => `/notification-engine/monitor/${id}/retry`,
      monitorCancel: (id: string) => `/notification-engine/monitor/${id}/cancel`,
    },
  },

  workflows: {
    catalog: '/workflows/catalog',
    settings: '/workflows/settings',
    list: '/workflows',
    get: (id: string) => `/workflows/${id}`,
    save: '/workflows',
    update: (id: string) => `/workflows/${id}`,
    remove: (id: string) => `/workflows/${id}`,
    activate: (id: string) => `/workflows/${id}/activate`,
    deactivate: (id: string) => `/workflows/${id}/deactivate`,
    archive: (id: string) => `/workflows/${id}/archive`,
    clone: (id: string) => `/workflows/${id}/clone`,
    versions: (id: string) => `/workflows/${id}/versions`,
    users: '/workflows/lookups/users',
    roles: '/workflows/lookups/roles',
    indicators: '/workflows/indicators',
    instances: '/workflows/instances',
    instance: (id: string) => `/workflows/instances/${id}`,
    history: (id: string) => `/workflows/instances/${id}/history`,
    approve: (id: string) => `/workflows/instances/${id}/approve`,
    reject: (id: string) => `/workflows/instances/${id}/reject`,
    return: (id: string) => `/workflows/instances/${id}/return`,
  },

  // Documents
  documents: {
    byEntity: (entityType: string, entityId: string) => `/documents/entity/${entityType}/${entityId}`,
    upload: '/documents/upload',
    download: (id: string) => `/documents/${id}/download`,
    delete: (id: string) => `/documents/${id}`,
    get: (id: string) => `/documents/${id}`,
  },

  // Document Storage Settings
  documentStorageSettings: {
    get: (companyId?: string) => companyId ? `/documentstoragesettings?companyId=${companyId}` : '/documentstoragesettings',
    save: (companyId?: string) => companyId ? `/documentstoragesettings?companyId=${companyId}` : '/documentstoragesettings',
  },

  // Eyewear Prescription
  eyewearPrescription: {
    list: '/eyewearprescription',
    get: (id: string) => `/eyewearprescription/${id}`,
    byInvoiceId: (invoiceId: string | number) => `/eyewearprescription/invoice/${invoiceId}`,
    byContactId: (contactId: string) => `/eyewearprescription/contact/${contactId}`,
    create: '/eyewearprescription',
    update: (id: string) => `/eyewearprescription/${id}`,
    delete: (id: string) => `/eyewearprescription/${id}`,
  },

  // Search
  search: {
    global: (query: string, page: number = 1, pageSize: number = 20) => 
      `/search/global?query=${encodeURIComponent(query)}&page=${page}&pageSize=${pageSize}`,
  },

  // Analytics
  analytics: {
    customerBehavior: '/analytics/customer-behavior',
    salesTrends: '/analytics/sales-trends',
    topSellingItems: '/analytics/top-selling-items',
  },

  // Auth
  auth: {
    login: '/auth/login',
    logout: '/auth/logout',
    refresh: '/auth/refresh',
    auditLogin: '/auth/audit-login',
    me: '/auth/me',
    changePassword: '/auth/change-password',
    verifyPassword: '/auth/verify-password',
    updateProfile: '/auth/profile',
    personalDefaults: '/auth/personal-defaults',
  },

  // Dashboard
  dashboard: {
    summary: (branchId?: string) => branchId ? `/dashboard/summary?branchId=${branchId}` : '/dashboard/summary',
    salesChart: (branchId?: string, days?: number) => {
        let url = '/dashboard/sales-chart?';
        if (branchId) url += `branchId=${branchId}&`;
        if (days) url += `days=${days}`;
        return url;
    },
    peakHours: (branchId?: string, startDate?: string, endDate?: string) => {
        const params = new URLSearchParams();
        if (branchId) params.append('branchId', branchId);
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        return `/dashboard/peak-hours?${params.toString()}`;
    },
    salesByBranch: (startDate?: string, endDate?: string) => {
        const params = new URLSearchParams();
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        return `/dashboard/sales-by-branch?${params.toString()}`;
    },
    topProfitableItems: (branchId?: string, startDate?: string, endDate?: string, top?: number) => {
        const params = new URLSearchParams();
        if (branchId) params.append('branchId', branchId);
        if (startDate) params.append('startDate', startDate);
        if (endDate) params.append('endDate', endDate);
        if (top) params.append('top', top.toString());
        return `/dashboard/top-profitable-items?${params.toString()}`;
    },
  },

  // Employee Tasks
  employeeTasks: {
    list: (branchId?: string, assignedToId?: string, statusId?: number) => {
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId);
      if (assignedToId) params.append('assignedToId', assignedToId);
      if (statusId) params.append('statusId', statusId.toString());
      return `/employeetasks?${params.toString()}`;
    },
    get: (id: string) => `/employeetasks/${id}`,
    create: '/employeetasks',
    update: (id: string) => `/employeetasks/${id}`,
    delete: (id: string) => `/employeetasks/${id}`,
    updateStatus: (id: string) => `/employeetasks/${id}/status`,
  },

  // HR
  hr: {
    employees: {
      list: '/hr/employees',
      get: (id: string) => `/hr/employees/${id}`,
      create: '/hr/employees',
      update: (id: string) => `/hr/employees/${id}`,
      delete: (id: string) => `/hr/employees/${id}`,
      linkUser: '/hr/employees/link-user',
    },
    departments: {
      list: '/hr/departments',
      get: (id: string) => `/hr/departments/${id}`,
      create: '/hr/departments',
      update: (id: string) => `/hr/departments/${id}`,
      delete: (id: string) => `/hr/departments/${id}`,
    },
    jobProfiles: {
      list: '/hr/job-profiles',
      get: (id: string) => `/hr/job-profiles/${id}`,
      create: '/hr/job-profiles',
      update: (id: string) => `/hr/job-profiles/${id}`,
      delete: (id: string) => `/hr/job-profiles/${id}`,
    },
    contracts: {
      list: '/hr/contracts',
      get: (id: string) => `/hr/contracts/${id}`,
      create: '/hr/contracts',
      update: (id: string) => `/hr/contracts/${id}`,
      delete: (id: string) => `/hr/contracts/${id}`,
    },
    leaveRequests: {
      list: '/hr/leave-requests',
      get: (id: string) => `/hr/leave-requests/${id}`,
      create: '/hr/leave-requests',
      update: (id: string) => `/hr/leave-requests/${id}`,
      delete: (id: string) => `/hr/leave-requests/${id}`,
      updateStatus: (id: string) => `/hr/leave-requests/${id}/status`,
    },
    attendance: {
      list: '/hr/attendance',
      get: (id: string) => `/hr/attendance/${id}`,
      create: '/hr/attendance',
      update: (id: string) => `/hr/attendance/${id}`,
      delete: (id: string) => `/hr/attendance/${id}`,
      checkIn: '/hr/attendance/check-in',
      checkOut: '/hr/attendance/check-out',
      today: '/hr/attendance/today',
      myEmployee: '/hr/attendance/my-employee',
      breaks: '/hr/attendance/breaks',
      breakStart: '/hr/attendance/break-start',
      breakEnd: '/hr/attendance/break-end',
      policies: {
        list: '/hr/attendance/policies',
        create: '/hr/attendance/policies',
        update: (id: string) => `/hr/attendance/policies/${id}`,
        delete: (id: string) => `/hr/attendance/policies/${id}`,
      },
      penaltyRules: {
        list: '/hr/attendance/penalty-rules',
        create: '/hr/attendance/penalty-rules',
        update: (id: string) => `/hr/attendance/penalty-rules/${id}`,
        delete: (id: string) => `/hr/attendance/penalty-rules/${id}`,
      },
    },
    attendanceDevices: {
      list: '/hr/attendance-devices',
      get: (id: string) => `/hr/attendance-devices/${id}`,
      create: '/hr/attendance-devices',
      update: (id: string) => `/hr/attendance-devices/${id}`,
      delete: (id: string) => `/hr/attendance-devices/${id}`,
      users: (id: string) => `/hr/attendance-devices/${id}/users`,
      reprocess: (id: string) => `/hr/attendance-devices/${id}/reprocess`,
    },
    attendancePunches: {
      list: '/hr/attendance-punches',
      create: '/hr/attendance-punches',
      import: '/hr/attendance-punches/import',
      reprocess: '/hr/attendance-punches/reprocess',
      delete: (id: string) => `/hr/attendance-punches/${id}`,
    },
    attendanceSites: {
      list: '/hr/attendance-sites',
      get: (id: string) => `/hr/attendance-sites/${id}`,
      create: '/hr/attendance-sites',
      update: (id: string) => `/hr/attendance-sites/${id}`,
      delete: (id: string) => `/hr/attendance-sites/${id}`,
      evaluate: '/hr/attendance-sites/evaluate',
      employees: (id: string) => `/hr/attendance-sites/${id}/employees`,
    },
    attendanceAppDevices: {
      list: '/hr/attendance-app-devices',
      status: '/hr/attendance-app-devices/status',
      enroll: '/hr/attendance-app-devices/enroll',
      challenge: '/hr/attendance-app-devices/challenge',
      revoke: (id: string) => `/hr/attendance-app-devices/${id}`,
    },
    workSchedules: {
      list: '/hr/work-schedules',
      get: (id: string) => `/hr/work-schedules/${id}`,
      create: '/hr/work-schedules',
      update: (id: string) => `/hr/work-schedules/${id}`,
      delete: (id: string) => `/hr/work-schedules/${id}`,
      assignments: '/hr/employee-work-schedules',
      assignmentUpdate: (id: string) => `/hr/employee-work-schedules/${id}`,
      assignmentDelete: (id: string) => `/hr/employee-work-schedules/${id}`,
    },
    timeEntries: {
      list: '/hr/time-entries',
      get: (id: string) => `/hr/time-entries/${id}`,
      create: '/hr/time-entries',
      update: (id: string) => `/hr/time-entries/${id}`,
      delete: (id: string) => `/hr/time-entries/${id}`,
    },
    employeeAdvances: {
      list: '/hr/employee-advances',
      get: (id: string) => `/hr/employee-advances/${id}`,
      create: '/hr/employee-advances',
      approve: (id: string) => `/hr/employee-advances/${id}/approve`,
      delete: (id: string) => `/hr/employee-advances/${id}`,
      summary: '/hr/employee-advances/summary',
    },
    employeeDeductions: {
      list: '/hr/employee-deductions',
      get: (id: string) => `/hr/employee-deductions/${id}`,
      create: '/hr/employee-deductions',
      delete: (id: string) => `/hr/employee-deductions/${id}`,
    },
    payroll: {
      list: '/hr/payroll',
      get: (id: string) => `/hr/payroll/${id}`,
      calculate: '/hr/payroll/calculate',
      create: '/hr/payroll',
      post: (id: string) => `/hr/payroll/${id}/post`,
      pay: (id: string) => `/hr/payroll/${id}/pay`,
      delete: (id: string) => `/hr/payroll/${id}`,
    },
  },

  // Kitchen Display System
  kitchen: {
    orders: (branchId: string) => `/kitchen?branchId=${branchId}`,
    updateStatus: (id: number) => `/kitchen/${id}/status`,
    scanQR: (id: number) => `/kitchen/${id}/scan-qr`,
    deliveryAccept: (id: number) => `/kitchen/${id}/delivery-accept`,
  },

  // Companies
  companies: {
    list: '/companies',
    get: (id: string) => `/companies/${id}`,
    create: '/companies',
    update: (id: string) => `/companies/${id}`,
    delete: (id: string) => `/companies/${id}`,
    invoiceAccountConstants: (id: string) => `/companies/${id}/invoice-account-constants`,
    patchAccountConstants: (id: string) => `/companies/${id}/account-constants`,
    updateAccountMapping: (id: string) => `/companies/${id}/account-mapping`,
    accountConstantSuggestions: (id: string) => `/companies/${id}/account-constant-suggestions`,
  },

  companyScope: {
    status: '/companyscope/status',
    setStrictIsolation: '/companyscope/strict-isolation',
  },

  // Fiscal Years
  fiscalYears: {
    list: (companyId?: string) => companyId ? `/fiscalyears?companyId=${companyId}` : '/fiscalyears',
    get: (id: string) => `/fiscalyears/${id}`,
    create: '/fiscalyears',
    update: (id: string) => `/fiscalyears/${id}`,
    delete: (id: string) => `/fiscalyears/${id}`,
    close: (id: string) => `/fiscalyears/${id}/close`,
    active: (companyId: string) => `/fiscalyears/company/${companyId}/active`,
    validateClose: (id: string) => `/fiscalyears/${id}/validate-close`,
    yearEndClose: (id: string) => `/fiscalyears/${id}/year-end-close`,
    reopen: (id: string) => `/fiscalyears/${id}/reopen`,
    canReopen: (id: string) => `/fiscalyears/${id}/can-reopen`,
    closingReport: (id: string) => `/fiscalyears/${id}/closing-report`,
    canModify: (companyId: string, operationDate: string) => `/fiscalyears/can-modify?companyId=${companyId}&operationDate=${operationDate}`,
    preCloseCheck: (id: string) => `/fiscalyears/${id}/pre-close-check`,
  },

  // Budgets
  budgets: {
    list: '/budgets',
    get: (id: string) => `/budgets/${id}`,
    create: '/budgets',
    update: (id: string) => `/budgets/${id}`,
    delete: (id: string) => `/budgets/${id}`,
    approve: (id: string) => `/budgets/${id}/approve`,
    activate: (id: string) => `/budgets/${id}/activate`,
    close: (id: string) => `/budgets/${id}/close`,
    analysis: (id: string) => `/budgets/${id}/analysis`,
    varianceReport: (id: string) => `/budgets/${id}/variance-report`,
    warnings: (id: string) => `/budgets/${id}/warnings`,
    recalculate: (id: string) => `/budgets/${id}/recalculate`,
    active: (companyId: string) => `/budgets/company/${companyId}/active`,
  },

  // Bank Reconciliation
  bankReconciliations: {
    list: (branchId?: string, accountId?: string, fromDate?: string, toDate?: string) => {
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId);
      if (accountId) params.append('accountId', accountId);
      if (fromDate) params.append('fromDate', fromDate);
      if (toDate) params.append('toDate', toDate);
      return `/bankreconciliations?${params.toString()}`;
    },
    get: (id: string) => `/bankreconciliations/${id}`,
    create: '/bankreconciliations',
    addStatementLine: (id: string) => `/bankreconciliations/${id}/statement-lines`,
    ledger: (accountId: string, branchId?: string, fromDate?: string, toDate?: string) => {
      const params = new URLSearchParams();
      params.append('accountId', accountId);
      if (branchId) params.append('branchId', branchId);
      if (fromDate) params.append('fromDate', fromDate);
      if (toDate) params.append('toDate', toDate);
      return `/bankreconciliations/ledger?${params.toString()}`;
    },
    match: (id: string) => `/bankreconciliations/${id}/match`,
    unmatch: (id: string) => `/bankreconciliations/${id}/unmatch`,
  },

  // Smart Stock Take (Inventory Count)
  smartStockTake: {
    list: '/smartstocktake',
    get: (id: string) => `/smartstocktake/${id}`,
    summary: (id: string) => `/smartstocktake/${id}/summary`,
    start: '/smartstocktake/start',
    complete: (id: string) => `/smartstocktake/${id}/complete`,
    approve: (id: string) => `/smartstocktake/${id}/approve`,
    cancel: (id: string) => `/smartstocktake/${id}/cancel`,
    updateItem: (id: string) => `/smartstocktake/items/${id}`,
    deleteItem: (id: string) => `/smartstocktake/items/${id}`,
    lines: (sessionId: string) => `/smartstocktake/${sessionId}/lines`,
    export: (sessionId: string, includeSystemQty?: boolean) =>
      `/smartstocktake/${sessionId}/export${includeSystemQty === undefined ? '' : `?includeSystemQty=${includeSystemQty}`}`,
    import: (sessionId: string) => `/smartstocktake/${sessionId}/import`,
  },

  // Budget Lines
  budgetLines: {
    list: '/budgetlines',
    get: (id: string) => `/budgetlines/${id}`,
    create: '/budgetlines',
    update: (id: string) => `/budgetlines/${id}`,
    delete: (id: string) => `/budgetlines/${id}`,
    byBudget: (budgetId: string) => `/budgetlines?budgetId=${budgetId}`,
  },

  // Branches
  branches: {
    list: '/branches',
    get: (id: string) => `/branches/${id}`,
    create: '/branches',
    update: (id: string) => `/branches/${id}`,
    delete: (id: string) => `/branches/${id}`,
    uploadLogo: (id: string) => `/branches/${id}/logo`,
  },

  // Stores
  stores: {
    list: '/stores',
    get: (id: string) => `/stores/${id}`,
    create: '/stores',
    update: (id: string) => `/stores/${id}`,
    delete: (id: string) => `/stores/${id}`,
  },

  // Periodic Inventory
  periodicInventory: {
    close: '/periodic-inventory/close',
  },

  // Tables
  tables: {
    list: '/tables',
    get: (id: number) => `/tables/${id}`,
    create: '/tables',
    update: (id: number) => `/tables/${id}`,
    delete: (id: number) => `/tables/${id}`,
    updateOccupied: (id: number) => `/tables/${id}/occupied`,
  },

  // Book Locations
  bookLocations: {
    list: '/booklocations',
    get: (id: number) => `/booklocations/${id}`,
    create: '/booklocations',
    update: (id: number) => `/booklocations/${id}`,
    delete: (id: number) => `/booklocations/${id}`,
    updateOccupied: (id: number) => `/booklocations/${id}/occupied`,
  },

  // Items
  items: {
    list: '/items',
    /** Resolves main barcode and supplemental barcodes (server-side). */
    byBarcode: (barcode: string) => `/items/barcode/${encodeURIComponent(barcode)}`,
    get: (id: string) => `/items/${id}`,
    create: '/items',
    update: (id: string) => `/items/${id}`,
    delete: (id: string) => `/items/${id}`,
    import: '/items/import',
    importTemplate: '/items/import/template',
    bulkUpdatePrices: '/items/bulk-update-prices',
    batchPriceAdjust: '/items/batch-price-adjust',
    mergePreview: '/items/merge/preview',
    merge: '/items/merge',
  },

  // Contacts
  contacts: {
    list: '/contacts',
    get: (id: string) => `/contacts/${id}`,
    create: '/contacts',
    update: (id: string) => `/contacts/${id}`,
    delete: (id: string) => `/contacts/${id}`,
  },

  // // Customers (uses contacts endpoint with ContactType='C')
  // customers: {
  //   list: '/contacts',
  //   get: (id: string) => `/contacts/${id}`,
  //   create: '/contacts',
  //   update: (id: string) => `/contacts/${id}`,
  //   delete: (id: string) => `/contacts/${id}`,
  // },

  // Customers
  customers: {
    list: '/customers',
    get: (id: string) => `/customers/${id}`,
    create: '/customers',
    update: (id: string) => `/customers/${id}`,
    delete: (id: string) => `/customers/${id}`,
    accountStatement: (id: string) => `/customers/${id}/account-statement`,
  },

  // Vendors
  vendors: {
    list: '/vendors',
    get: (id: string) => `/vendors/${id}`,
    create: '/vendors',
    update: (id: string) => `/vendors/${id}`,
    delete: (id: string) => `/vendors/${id}`,
  },

  contactAccounts: {
    linkedByContact: (contactId: string, isCustomer: boolean) =>
      `/contactaccounts/linked/${encodeURIComponent(contactId)}?isCustomer=${isCustomer}`,
    linked: (isCustomer: boolean, branchId?: string, currentParentAccountId?: string) => {
      const params = new URLSearchParams({ isCustomer: String(isCustomer) })
      if (branchId) params.set('branchId', branchId)
      if (currentParentAccountId) params.set('currentParentAccountId', currentParentAccountId)
      return `/contactaccounts/linked?${params.toString()}`
    },
    underParent: (parentAccountId: string, isCustomer: boolean, branchId?: string) => {
      const params = new URLSearchParams({ parentAccountId, isCustomer: String(isCustomer) })
      if (branchId) params.set('branchId', branchId)
      return `/contactaccounts/under-parent?${params.toString()}`
    },
    validateTarget: (parentAccountId: string) =>
      `/contactaccounts/validate-target?parentAccountId=${encodeURIComponent(parentAccountId)}`,
    transfer: '/contactaccounts/transfer',
  },

  // Customer Vendors
  customerVendors: {
    base: '/customervendors',
    list: '/customervendors',
    get: (id: string) => `/customervendors/${id}`,
    getByContactId: (contactId: string) => `/customervendors?contactId=${contactId}`,
    create: '/customervendors',
    update: (id: string) => `/customervendors/${id}`,
    delete: (id: string) => `/customervendors/${id}`,
  },

  // Dashboard Widgets
  dashboardWidgets: {
    list: '/dashboardwidgets',
    all: '/dashboardwidgets/list-all',
    get: (id: string) => `/dashboardwidgets/${id}`,
    create: '/dashboardwidgets',
    update: (id: string) => `/dashboardwidgets/${id}`,
    updateLayout: (id: string) => `/dashboardwidgets/${id}/layout`,
    updateVisibility: (id: string) => `/dashboardwidgets/${id}/visibility`,
    delete: (id: string) => `/dashboardwidgets/${id}`,
  },

  // Chart of Accounts
  chartOfAccounts: {
    list: '/chartofaccounts',
    tree: '/chartofaccounts/tree',
    root: (branchId?: string) => branchId ? `/chartofaccounts/root?branchId=${branchId}` : '/chartofaccounts/root',
    leaf: '/chartofaccounts/leaf',
    lookup: '/chartofaccounts/lookup',
    get: (id: string) => `/chartofaccounts/${id}`,
    create: '/chartofaccounts',
    update: (id: string) => `/chartofaccounts/${id}`,
    delete: (id: string) => `/chartofaccounts/${id}`,
    bulkDelete: '/chartofaccounts/bulk-delete',
    move: (id: string) => `/chartofaccounts/${id}/move`,
    missingCachedNumbers: '/chartofaccounts/missing-cached-numbers',
    backfillCachedNumbers: '/chartofaccounts/backfill-cached-numbers',
    children: (id: string, branchId?: string) => branchId ? `/chartofaccounts/${id}/children?branchId=${branchId}` : `/chartofaccounts/${id}/children`,
    backup: {
      create: '/chartofaccounts/backup',
      list: '/chartofaccounts/backups',
      restore: (backupId: string) => `/chartofaccounts/restore/${backupId}`,
    },
    import: {
      upload: '/chartofaccounts/import',
      preview: '/chartofaccounts/import/preview',
    },
    mergePreview: '/chartofaccounts/merge/preview',
    merge: '/chartofaccounts/merge',
  },

  // Cost Centers
  costCenters: {
    list: '/costcenters',
    tree: '/costcenters/tree',
    get: (id: string) => `/costcenters/${id}`,
    create: '/costcenters',
    update: (id: string) => `/costcenters/${id}`,
    delete: (id: string) => `/costcenters/${id}`,
    move: (id: string) => `/costcenters/${id}/move`,
    children: (id: string) => `/costcenters/${id}/children`,
  },

  // Inventory
  inventory: {
    list: '/inventory',
    get: (id: string) => `/inventory/${id}`,
    create: '/inventory',
    update: (id: string) => `/inventory/${id}`,
    lowStock: '/inventory/low-stock',
    summary: '/inventory/summary',
    movements: '/inventory/movements',
    transfer: '/inventory/transfer',
    stores: '/inventory/stores',
    items: '/inventory/items',
  },

  // Inventory Adjustments
  inventoryAdjustments: {
    list: '/inventoryadjustments',
    get: (id: string) => `/inventoryadjustments/${id}`,
    create: '/inventoryadjustments',
    update: (id: string) => `/inventoryadjustments/${id}`,
    delete: (id: string) => `/inventoryadjustments/${id}`,
  },

  // Inventory Stock
  inventoryStock: {
    list: '/inventorystock',
    get: (itemId: string, storeId: string, unitId?: string) => {
      const params = new URLSearchParams()
      if (itemId) params.append('itemId', itemId)
      if (storeId) params.append('storeId', storeId)
      if (unitId) params.append('unitId', unitId)
      return `/inventorystock?${params.toString()}`
    },
    transactions: '/inventorystock/transactions',
    summary: '/inventorystock/summary',
    lowStock: '/inventorystock/low-stock',
    in: '/inventorystock/in',
    out: '/inventorystock/out',
  },

  // Invoices
  invoices: {
    list: '/invoices',
    get: (id: string) => `/invoices/${id}`,
    create: '/invoices',
    bySaveKey: (key: string) => `/invoices/by-save-key/${encodeURIComponent(key)}`,
    customerItemPrice: (customerId: string, itemId: string, uomId?: number, priceNameId?: string) =>
      `/invoices/customer-item-price?itemId=${encodeURIComponent(itemId)}${customerId ? `&customerId=${encodeURIComponent(customerId)}` : ''}${uomId ? `&uomId=${uomId}` : ''}${priceNameId ? `&priceNameId=${encodeURIComponent(priceNameId)}` : ''}`,
    lastPrice: (customerId: string, itemId: string, uomId?: number) => `/invoices/last-price?customerId=${customerId}&itemId=${itemId}${uomId ? `&uomId=${uomId}` : ''}`,
    lastSalePrice: (itemId: string) => `/invoices/last-sale-price?itemId=${itemId}`,
    itemStock: (itemId: string, storeId?: string) =>
      `/invoices/item-stock?itemId=${encodeURIComponent(itemId)}${storeId ? `&storeId=${encodeURIComponent(storeId)}` : ''}`,
    availableExpiryDates: (itemId: string, storeId?: string) => `/invoices/available-expiry-dates/${itemId}${storeId ? `?storeId=${storeId}` : ''}`,
    stockForecasting: (itemId: string, branchId?: string) => {
      const params = new URLSearchParams()
      if (branchId) params.append('branchId', branchId)
      return `/inventory/forecasting/${itemId}?${params.toString()}`
    },
    soldExpiryDatesForReturn: (itemId: string, storeId?: string, saleDocId?: number) => {
      const params = new URLSearchParams()
      if (storeId) params.append('storeId', storeId)
      if (saleDocId) params.append('saleDocId', saleDocId.toString())
      return `/invoices/sold-expiry-dates-for-return/${itemId}${params.toString() ? `?${params.toString()}` : ''}`
    },
    update: (id: string) => `/invoices/${id}`,
    cancel: (id: string) => `/invoices/${id}/cancel`,
    cancelledPendingRepair: '/invoices/cancelled-pending-repair',
    repairCancelled: '/invoices/repair-cancelled',
    repairCancel: (id: string | number) => `/invoices/${id}/repair-cancel`,
    delete: (id: string) => `/invoices/${id}`,
    suspended: (branchId?: string) => branchId ? `/invoices/suspended?branchId=${branchId}` : '/invoices/suspended',
    suspend: (id: string) => `/invoices/${id}/suspend`,
    settle: (id: string | number) => `/invoices/${id}/settle`,
    settleMultiple: '/invoices/settle-multiple',
    merge: '/invoices/merge',
    outstandingByContact: (contactId: string) => `/invoices/outstanding-by-contact/${contactId}`,
    restore: (id: string) => `/invoices/${id}/restore`,
    fixableStockIssues: '/invoices/fixable-stock-issues',
    fixStock: (id: string) => `/invoices/${id}/fix-stock`,
    adjustCost: '/invoices/adjust-cost',
    generateJournalEntry: (id: string | number) => `/invoices/${id}/generate-journal-entry`,
    previewJournal: '/invoices/preview-journal',
  },

  accountingCopilot: {
    explain: '/accounting-copilot/explain',
  },

  developerPortal: {
    openapi: '/developer-portal/openapi',
    meta: '/developer-portal/meta',
    execute: '/developer-portal/execute',
    logs: '/developer-portal/logs',
    applications: '/developer-portal/applications',
    webhookEvents: '/developer-portal/webhook-events',
  },

  // Sales Orders (no inventory impact)
  salesOrders: {
    list: '/salesorders',
    get: (id: string | number) => `/salesorders/${id}`,
    create: '/salesorders',
    update: (id: string | number) => `/salesorders/${id}`,
    delete: (id: string | number) => `/salesorders/${id}`,
    convertToInvoice: (id: string | number) => `/salesorders/${id}/convert-to-invoice`,
  },

  purchaseOrders: {
    list: '/purchaseorders',
    get: (id: string | number) => `/purchaseorders/${id}`,
    create: '/purchaseorders',
    update: (id: string | number) => `/purchaseorders/${id}`,
    delete: (id: string | number) => `/purchaseorders/${id}`,
    convertToPurchaseInvoice: (id: string | number) => `/purchaseorders/${id}/convert-to-purchase-invoice`,
  },

  goodsReceipts: {
    create: '/goodsreceipts',
  },

  // Quotations (price quotes that can be converted to invoices)
  quotations: {
    list: '/quotations',
    get: (id: string | number) => `/quotations/${id}`,
    create: '/quotations',
    update: (id: string | number) => `/quotations/${id}`,
    delete: (id: string | number) => `/quotations/${id}`,
    convertToInvoice: (id: string | number) => `/quotations/${id}/convert-to-invoice`,
  },

  // Purchase Invoices
  purchaseInvoices: {
    list: '/purchaseinvoices',
    get: (id: string) => `/purchaseinvoices/${id}`,
    create: '/purchaseinvoices',
    bySaveKey: (key: string) => `/purchaseinvoices/by-save-key/${encodeURIComponent(key)}`,
    previewJournal: '/purchaseinvoices/preview-journal',
    update: (id: string) => `/purchaseinvoices/${id}`,
    delete: (id: string) => `/purchaseinvoices/${id}`,
    settle: (id: string | number) => `/purchaseinvoices/${id}/settle`,
    settleMultiple: '/purchaseinvoices/settle-multiple',
    outstandingByVendor: (vendorId: string) => `/purchaseinvoices/outstanding-by-vendor/${encodeURIComponent(vendorId)}`,
    openForPayment: '/purchaseinvoices/open-for-payment',
    paymentVouchers: (id: string | number) => `/purchaseinvoices/${id}/payment-vouchers`,
    generateJournalEntry: (id: string | number) => `/purchaseinvoices/${id}/generate-journal-entry`,
    lastPrice: (vendorId: string, itemId: string, uomId?: number) => `/purchaseinvoices/last-price?vendorId=${vendorId}&itemId=${itemId}${uomId ? `&uomId=${uomId}` : ''}`,
    fixableSalesInvoices: (id: string) => `/purchaseinvoices/${id}/fixable-sales-invoices`,
    fixRelatedSalesInvoices: (id: string) => `/purchaseinvoices/${id}/fix-related-sales-invoices`,
    purchasedExpiryDatesForReturn: (itemId: string, storeId?: string, purchaseDocId?: number) => {
      const params = new URLSearchParams()
      if (storeId) params.append('storeId', storeId)
      if (purchaseDocId) params.append('purchaseDocId', purchaseDocId.toString())
      return `/purchaseinvoices/purchased-expiry-dates-for-return/${itemId}${params.toString() ? `?${params.toString()}` : ''}`
    },
    purchaseReturnExpiryDatesForReturn: (itemId: string, storeId?: string, purchaseDocId?: number) => {
      const params = new URLSearchParams()
      if (storeId) params.append('storeId', storeId)
      if (purchaseDocId) params.append('purchaseDocId', purchaseDocId.toString())
      return `/purchaseinvoices/purchase-return-expiry-dates-for-return/${itemId}${params.toString() ? `?${params.toString()}` : ''}`
    },
  },

  landedCosts: {
    list: '/landedcosts',
    get: (id: string | number) => `/landedcosts/${id}`,
    create: '/landedcosts',
    update: (id: string | number) => `/landedcosts/${id}`,
    delete: (id: string | number) => `/landedcosts/${id}`,
    post: (id: string | number) => `/landedcosts/${id}/post`,
    cancel: (id: string | number) => `/landedcosts/${id}/cancel`,
    byPurchaseInvoice: (purchaseInvoiceId: string | number) => `/landedcosts/by-purchase-invoice/${purchaseInvoiceId}`,
    invoiceLines: (purchaseInvoiceId: string | number) => `/landedcosts/purchase-invoice/${purchaseInvoiceId}/lines`,
  },

  // Opening Stock Invoices (فاتورة أول مدة للمخزون)
  openingStockInvoices: {
    list: '/openingstockinvoices',
    get: (id: string | number) => `/openingstockinvoices/${id}`,
    journalEntry: (id: string | number) => `/openingstockinvoices/${id}/journal-entry`,
    syncJournalEntry: (id: string | number) => `/openingstockinvoices/${id}/journal-entry/sync`,
    journalEntrySettings: (branchId?: string, invoiceType?: string) => {
      const params = new URLSearchParams()
      if (branchId) params.append('branchId', branchId)
      if (invoiceType) params.append('invoiceType', invoiceType)
      return `/openingstockinvoices/journal-entry-settings${params.toString() ? `?${params.toString()}` : ''}`
    },
    create: '/openingstockinvoices',
    update: (id: string | number) => `/openingstockinvoices/${id}`,
    delete: (id: string | number) => `/openingstockinvoices/${id}`,
  },

  // Operations
  operations: {
    list: '/operations',
    get: (id: string) => `/operations/${id}`,
    create: '/operations',
    update: (id: string) => `/operations/${id}`,
    details: (id: string) => `/operations/${id}/details`,
    byInvoice: (invoiceId: number, operationType_ID?: string) => {
      const params = new URLSearchParams({ invoiceId: invoiceId.toString() })
      if (operationType_ID) {
        params.append('operationType_ID', operationType_ID)
      }
      return `/operations/by-invoice?${params.toString()}`
    },
    checkJournalEntries: (invoiceIds: number[], operationType_ID?: string) => {
      const params = new URLSearchParams({ invoiceIds: invoiceIds.join(',') })
      if (operationType_ID) {
        params.append('operationType_ID', operationType_ID)
      }
      return `/operations/check-journal-entries?${params.toString()}`
    },
  },

  // Journal Entries
  journalEntries: {
    list: '/journalentries',
    get: (id: string) => `/journalentries/${id}`,
    create: '/journalentries',
    update: (id: string) => `/journalentries/${id}`,
    approve: (id: string) => `/journalentries/${id}/approve`,
    unpost: (id: string) => `/journalentries/${id}/unpost`,
    reverse: (id: string) => `/journalentries/${id}/reverse`,
    forceDelete: (id: string) => `/journalentries/${id}/force-delete`,
  },

  // Vouchers (سندات القبض والصرف)
  vouchers: {
    list: '/vouchers',
    get: (id: string) => `/vouchers/${id}`,
    create: '/vouchers',
    bySaveKey: (key: string) => `/vouchers/by-save-key/${encodeURIComponent(key)}`,
    update: (id: string) => `/vouchers/${id}`,
    delete: (id: string) => `/vouchers/${id}`,
    post: (id: string) => `/vouchers/${id}/post`,
    cancel: (id: string) => `/vouchers/${id}/cancel`,
    summary: '/vouchers/summary',
    nextNumber: '/vouchers/next-number',
  },

  // Recurring Journal Entries
  recurringJournalEntries: {
    list: '/recurringjournalentries',
    get: (id: string) => `/recurringjournalentries/${id}`,
    create: '/recurringjournalentries',
    update: (id: string) => `/recurringjournalentries/${id}`,
    delete: (id: string) => `/recurringjournalentries/${id}`,
    generate: (id: string) => `/recurringjournalentries/${id}/generate`,
    generateAll: '/recurringjournalentries/generate-all',
  },

  // Webhooks
  webhooks: {
    list: '/webhooks',
    get: (id: string) => `/webhooks/${id}`,
    create: '/webhooks',
    update: (id: string) => `/webhooks/${id}`,
    delete: (id: string) => `/webhooks/${id}`,
    logs: (id: string) => `/webhooks/${id}/logs`,
    test: (id: string) => `/webhooks/${id}/test`,
    events: '/webhooks/events',
  },

  // Patients (Clinic)
  patients: {
    list: (search?: string, page: number = 1, pageSize: number = 10, branchId?: string) => {
      const params = new URLSearchParams()
      if (search) params.append('search', search)
      params.append('page', page.toString())
      params.append('pageSize', pageSize.toString())
      if (branchId) params.append('branchId', branchId)
      return `/patients?${params.toString()}`
    },
    get: (id: string) => `/patients/${id}`,
    create: '/patients',
    update: (id: string) => `/patients/${id}`,
    medicalHistory: (id: string) => `/patients/${id}/medical-history`,
    addDocument: (id: string) => `/patients/${id}/documents`,
    insurance: (contactId: string) => `/insurance/patient/${contactId}`,
    saveInsurance: '/insurance/patient',
    companies: '/insurance/companies',
  },

  // Dental Dashboard & Reports
  dentalDashboard: {
    stats: (branchId?: string) => `/dentalDashboard/stats${branchId ? `?branchId=${branchId}` : ''}`,
    topServices: (count: number = 5) => `/dentalDashboard/top-services?count=${count}`,
    peakTimes: '/dentalDashboard/peak-times',
    alerts: '/dentalDashboard/alerts',
    doctorPerformance: (from?: string, to?: string) => {
      const params = new URLSearchParams()
      if (from) params.append('from', from)
      if (to) params.append('to', to)
      return `/dentalDashboard/doctor-performance?${params.toString()}`
    },
  },

  // Dental Implants Management
  implants: {
    cases: (contactId?: string, branchId?: string) => {
      const params = new URLSearchParams()
      if (contactId) params.append('contactId', contactId)
      if (branchId) params.append('branchId', branchId)
      return `/implants/cases?${params.toString()}`
    },
    getCase: (id: number) => `/implants/cases/${id}`,
    createCase: '/implants/cases',
    updateCase: (id: number) => `/implants/cases/${id}`,
    addDetail: '/implants/details',
    updateStage: (id: number) => `/implants/stages/${id}`,
    expiringWarranties: (days: number = 30) => `/implants/expiring-warranties?days=${days}`,
  },

  // Prescriptions
  prescriptions: {
    list: (contactId?: string) => contactId ? `/prescriptions?contactId=${contactId}` : '/prescriptions',
    get: (id: number) => `/prescriptions/${id}`,
    create: '/prescriptions',
    delete: (id: number) => `/prescriptions/${id}`,
  },

  // Dental Lab Orders
  labOrders: {
    list: (contactId?: string, status?: string) => {
      const params = new URLSearchParams()
      if (contactId) params.append('contactId', contactId)
      if (status) params.append('status', status)
      return `/dentalLabOrders?${params.toString()}`
    },
    get: (id: number) => `/dentalLabOrders/${id}`,
    create: '/dentalLabOrders',
    updateStatus: (id: number) => `/dentalLabOrders/${id}/status`,
    delete: (id: number) => `/dentalLabOrders/${id}`,
  },

  // Treatment Plans
  treatmentPlans: {
    list: (contactId?: string) => contactId ? `/treatmentPlans?contactId=${contactId}` : '/treatmentPlans',
    get: (id: number) => `/treatmentPlans/${id}`,
    create: '/treatmentPlans',
    updateItemStatus: (itemId: number) => `/treatmentPlans/items/${itemId}/status`,
  },

  // Appointments (Clinic Scheduling)
  appointments: {
    list: (start: string, end: string, branchId?: string) => {
      const params = new URLSearchParams({ start, end })
      if (branchId) params.append('branchId', branchId)
      return `/appointments?${params.toString()}`
    },
    create: '/appointments',
    update: (id: number) => `/appointments/${id}`,
    rooms: (branchId?: string) => {
      const params = new URLSearchParams()
      if (branchId) params.append('branchId', branchId)
      return `/appointments/rooms?${params.toString()}`
    },
    createRoom: '/appointments/rooms',
    doctors: '/appointments/doctors',
  },

  ai: {
    catalog: '/ai/catalog',
    providers: '/ai/providers',
    provider: (id: string) => `/ai/providers/${id}`,
    testProvider: (id: string) => `/ai/providers/${id}/test`,
    conversations: '/ai/conversations',
    conversation: (id: string) => `/ai/conversations/${id}`,
    chat: '/ai/chat',
  },

  // Hungration Integration
  hungration: {
    settings: '/hungration/settings',
    test: '/hungration/test',
    sendInvoice: '/hungration/send-invoice',
  },

  // Talabat Integration
  talabat: {
    settings: '/talabat/settings',
    test: '/talabat/test',
    sendOrder: '/talabat/send-order',
    updateStatus: (orderId: string) => `/talabat/orders/${orderId}/status`,
  },

  // Jahez Integration
  jahez: {
    settings: '/jahez/settings',
    test: '/jahez/test',
    sendOrder: '/jahez/send-order',
    updateStatus: (orderId: string) => `/jahez/orders/${orderId}/status`,
  },

  // Salla Integration
  salla: {
    settings: '/salla/settings',
    test: '/salla/test',
    webhook: '/salla/webhook',
    fetchOrders: '/salla/fetch-orders',
    updateStatus: (orderId: string) => `/salla/orders/${orderId}/status`,
  },

  // Electronic Shelf Labels (ESL)
  esl: {
    settings: '/esl/settings',
    test: '/esl/test',
    tags: '/esl/tags',
    tag: (id: number) => `/esl/tags/${id}`,
    syncItem: (itemId: string) => `/esl/sync/item/${itemId}`,
    syncAll: '/esl/sync/all',
    blink: (tagCode: string) => `/esl/tags/${encodeURIComponent(tagCode)}/blink`,
    logs: '/esl/logs',
    promotions: '/esl/promotions',
    processQueue: '/esl/queue/process',
  },

  // Import
  import: {
    housingUnits: '/import/housing-units',
    /** عدة أوراق: اسم التبويب = الحي */
    housingUnitsByNeighborhood: '/import/housing-units-by-neighborhood',
    importStatus: (jobId: string) => `/import/status/${jobId}`,
  },

  // Operation Types
  operationTypes: {
    list: '/operationtypes',
    get: (id: string | number) => `/operationtypes/${id}`,
    create: '/operationtypes',
    update: (id: string | number) => `/operationtypes/${id}`,
    delete: (id: string | number) => `/operationtypes/${id}`,
  },

  // Sales Reps
  salesReps: {
    list: '/salesreps',
    get: (id: number) => `/salesreps/${id}`,
    create: '/salesreps',
    update: (id: number) => `/salesreps/${id}`,
    delete: (id: number) => `/salesreps/${id}`,
  },

  // Sales Rep Mobile (requests & notifications)
  repMobile: {
    requests: '/repmobile/requests',
    approve: (id: number) => `/repmobile/requests/${id}/approve`,
    reject: (id: number) => `/repmobile/requests/${id}/reject`,
    notifyAdmin: '/repmobile/notify-admin',
    notifications: '/repmobile/notifications',
    updateLocation: '/repmobile/location',
    locations: '/repmobile/locations',
  },

  // Shifts (ورديات)
  shifts: {
    list: (branchId?: string, userId?: number, fromDate?: string, toDate?: string, status?: string) => {
      const params = new URLSearchParams()
      if (branchId) params.append('branchId', branchId)
      if (userId) params.append('userId', userId.toString())
      if (fromDate) params.append('fromDate', fromDate)
      if (toDate) params.append('toDate', toDate)
      if (status) params.append('status', status)
      const query = params.toString()
      return `/shifts${query ? `?${query}` : ''}`
    },
    get: (id: string) => `/shifts/${id}`,
    getActive: (branchId: string, userId?: number) => {
      const params = new URLSearchParams()
      params.append('branchId', branchId)
      if (userId) params.append('userId', userId.toString())
      return `/shifts/active?${params.toString()}`
    },
    open: '/shifts/open',
    close: (id: string) => `/shifts/${id}/close`,
    invoices: (id: string) => `/shifts/${id}/invoices`,
  },

  // Maintenance
  maintenance: {
    technicians: {
        list: '/maintenance/technicians',
        get: (id: string) => `/maintenance/technicians/${id}`,
        create: '/maintenance/technicians',
        update: (id: string) => `/maintenance/technicians/${id}`,
        delete: (id: string) => `/maintenance/technicians/${id}`,
    },
    workOrders: {
        list: (branchId?: string, status?: string, technicianId?: string) => {
            const params = new URLSearchParams();
            if (branchId) params.append('branchId', branchId);
            if (status) params.append('status', status);
            if (technicianId) params.append('technicianId', technicianId);
            return `/maintenance/work-orders?${params.toString()}`;
        },
        get: (id: string) => `/maintenance/work-orders/${id}`,
        create: '/maintenance/work-orders',
        update: (id: string) => `/maintenance/work-orders/${id}`,
        delete: (id: string) => `/maintenance/work-orders/${id}`,
        updateStatus: (id: string) => `/maintenance/work-orders/${id}/status`,
    },
    settings: {
        get: '/maintenance/settings',
        update: '/maintenance/settings',
    }
  },

  // Reports
  reports: {
    profitLoss: '/reports/profit-loss',
    balanceSheet: '/reports/balance-sheet',
    inventory: '/reports/inventory',
    deadStockTenders: '/reports/dead-stock-tenders',
    detailedSales: '/reports/detailed-sales-report',
    sallaSales: '/reports/salla-sales',
    comparison: '/reports/comparison',
    accountStatement: '/reports/account-statement',
    interactiveAccountStatement: '/reports/interactive-account-statement',
    customerAccountStatement: '/reports/customer-account-statement',
    vendorAccountStatement: '/reports/vendor-account-statement',
    customerAccountStatementWithItems: '/reports/customer-account-statement-with-items',
    accountBalances: '/reports/account-balances',
    generalLedger: '/reports/general-ledger',
    subsidiaryLedger: '/reports/subsidiary-ledger',
    trialBalance: '/reports/trial-balance',
    costOfSales: '/reports/cost-of-sales',
    costOfSalesAccounting: '/reports/cost-of-sales-accounting',
    incomeStatement: '/reports/income-statement',
    financialPosition: '/reports/financial-position',
    cashFlow: '/reports/cash-flow',
    inventoryCount: '/reports/inventory-count',
    negativeStockItems: '/reports/negative-stock-items',
    deferredCostInvoices: '/reports/deferred-cost-invoices',
    dailyMaterialsMovement: '/reports/daily-materials-movement',
    vatReturns: '/reports/vat-returns',
    vatReturnsCalculate: '/reports/vat-returns/calculate',
    vatReturnsSubmit: '/reports/vat-returns/submit',
    debtAging: '/reportfunction/DebtAging/execute',
    purchaseReport: '/reportfunction/PurchaseReport/execute',
    vatReturn: '/reportfunction/VatReturn/execute',
    salesRepReport: '/reportfunction/SalesRepReport/execute',
    salesRepItemsReport: '/reportfunction/SalesRepItemsReport/execute',
    salesRepAccountStatementByItems: '/reportfunction/SalesRepAccountStatementByItems/execute',
    salesRepCustomerBalances: '/reportfunction/SalesRepCustomerBalances/execute',
    itemMovementReport: '/reportfunction/ItemMovementReport/execute',
    inventoryStockReport: '/reportfunction/InventoryStockReport/execute',
    serialMovementReport: '/reportfunction/SerialMovementReport/execute',
    costCenters: '/reports/cost-centers',
    costCenterStatement: '/reports/cost-centers/statement',
    costCenterAccounts: '/reports/cost-centers/accounts',
    costCenterDocuments: '/reports/cost-centers/documents',
  },

  dynamicReports: {
    list: '/dynamic-reports',
    menu: '/dynamic-reports/menu',
    get: (id: number | string) => `/dynamic-reports/${id}`,
    create: '/dynamic-reports',
    update: (id: number | string) => `/dynamic-reports/${id}`,
    delete: (id: number | string) => `/dynamic-reports/${id}`,
    run: '/dynamic-reports/run',
    runById: (id: number | string) => `/dynamic-reports/${id}/run`,
    preview: '/dynamic-reports/preview',
    export: (id: number | string, format: string) => `/dynamic-reports/${id}/export?format=${encodeURIComponent(format)}`,
    lookup: (id: number | string, fieldName: string) => `/dynamic-reports/${id}/lookup/${encodeURIComponent(fieldName)}`,
    schemaObjects: '/dynamic-reports/schema-objects',
    objectColumns: (schema: string, name: string) =>
      `/dynamic-reports/schema-objects/${encodeURIComponent(schema)}/${encodeURIComponent(name)}/columns`,
    views: '/dynamic-reports/views',
    view: (schema: string, name: string) => `/dynamic-reports/views/${encodeURIComponent(schema)}/${encodeURIComponent(name)}`,
    viewColumns: (schema: string, name: string) => `/dynamic-reports/views/${encodeURIComponent(schema)}/${encodeURIComponent(name)}/columns`,
    viewPreview: (schema: string, name: string) => `/dynamic-reports/views/${encodeURIComponent(schema)}/${encodeURIComponent(name)}/preview`,
    previewSql: '/dynamic-reports/preview-sql',
    createView: '/dynamic-reports/views',
    updateView: (schema: string, name: string) => `/dynamic-reports/views/${encodeURIComponent(schema)}/${encodeURIComponent(name)}`,
    deleteView: (schema: string, name: string) => `/dynamic-reports/views/${encodeURIComponent(schema)}/${encodeURIComponent(name)}`,
  },

  // Users
  users: {
    list: '/users',
    get: (id: string) => `/users/${id}`,
    create: '/users',
    update: (id: string) => `/users/${id}`,
    delete: (id: string) => `/users/${id}`,
  },

  // Taxes
  taxes: {
    list: '/taxes',
    get: (id: string) => `/taxes/${id}`,
    create: '/taxes',
    update: (id: string) => `/taxes/${id}`,
    delete: (id: string) => `/taxes/${id}`,
  },

  // Currencies
  restaurant: {
    deliveryOrders: {
      list: (branchId?: string) => branchId ? `/restaurant/delivery-orders?branchId=${branchId}` : '/restaurant/delivery-orders',
      updateStatus: (id: number) => `/restaurant/delivery-orders/${id}/status`,
    },
    ordersQueue: {
      list: (branchId?: string) => branchId ? `/restaurant/orders-queue?branchId=${branchId}` : '/restaurant/orders-queue',
      callNext: '/restaurant/orders-queue/call-next',
    },
    menu: {
      list: '/restaurant/menu',
      updateItem: (id: string) => `/restaurant/menu/${id}`,
      categories: '/restaurant/categories',
    },
    expenses: {
      list: '/restaurant/expenses',
      get: (id: string) => `/restaurant/expenses/${id}`,
      create: '/restaurant/expenses',
      delete: (id: string) => `/restaurant/expenses/${id}`,
      categories: '/restaurant/expenses/categories',
      createCategory: '/restaurant/expenses/categories',
      updateCategory: (id: string) => `/restaurant/expenses/categories/${id}`,
      deleteCategory: (id: string) => `/restaurant/expenses/categories/${id}`,
      summary: '/restaurant/expenses/summary',
      reports: {
        daily: '/restaurant/expenses/reports/daily',
        monthly: '/restaurant/expenses/reports/monthly',
        yearly: '/restaurant/expenses/reports/yearly',
      },
    },
    reports: {
      dailyCashier: '/restaurant/reports/daily-cashier',
    }
  },

  exchangeRates: {
    list: (currencyId?: number) => currencyId ? `/exchangerates?currencyId=${currencyId}` : '/exchangerates',
    get: (id: string) => `/exchangerates/${id}`,
    activeForDate: (currencyId: string | number, date?: string) => {
      const params = new URLSearchParams({ currencyId: String(currencyId) })
      if (date) params.append('date', date)
      return `/exchangerates/active-for-date?${params.toString()}`
    },
    create: '/exchangerates',
    update: (id: string) => `/exchangerates/${id}`,
  },

  currencies: {
    unified: '/currencies/unified',
    setUnified: '/currencies/unified',
    list: '/currencies',
    get: (id: string) => `/currencies/${id}`,
    create: '/currencies',
    update: (id: string) => `/currencies/${id}`,
    delete: (id: string) => `/currencies/${id}`,
  },

  // Categories
  categories: {
    list: '/itemscategories',
    get: (id: string) => `/itemscategories/${id}`,
    create: '/itemscategories',
    update: (id: string) => `/itemscategories/${id}`,
    delete: (id: string) => `/itemscategories/${id}`,
  },

  // Customer Categories
  customerCategories: {
    list: '/customercategories',
    get: (id: string) => `/customercategories/${id}`,
    create: '/customercategories',
    update: (id: string) => `/customercategories/${id}`,
    delete: (id: string) => `/customercategories/${id}`,
  },

  // Municipality Online Sync
  municipalityOnlineSync: {
    pushData: '/municipalityonlinesync/push-data',
    pullDataFull: '/municipalityonlinesync/pull-data-full',
    pullPayments: '/municipalityonlinesync/pull-payments',
    pushHousingUnit: '/municipalityonlinesync/push-housing-unit',
    pullHousingUnits: '/municipalityonlinesync/pull-housing-units',
  },

  // General Attributes
  generalAttributes: {
    list: '/generalattributes',
    types: '/generalattributes/types',
    get: (id: string) => `/generalattributes/${id}`,
    create: '/generalattributes',
    update: (id: string) => `/generalattributes/${id}`,
    delete: (id: string) => `/generalattributes/${id}`,
  },

  // Payment Methods
  paymentMethods: {
    list: '/paymentmethods',
    get: (id: string) => `/paymentmethods/${id}`,
    create: '/paymentmethods',
    update: (id: string) => `/paymentmethods/${id}`,
    delete: (id: string) => `/paymentmethods/${id}`,
  },

  // Invoice Settings
  invoiceSettings: {
    get: '/invoicesettings',
    create: '/invoicesettings',
    update: (id: number) => `/invoicesettings/${id}`,
    delete: (id: number) => `/invoicesettings/${id}`,
  },

  // Invoice Designs
  invoiceDesigns: {
    list: '/invoicedesigns',
    default: '/invoicedesigns/default',
    get: (id: number) => `/invoicedesigns/${id}`,
    create: '/invoicedesigns',
    update: (id: number) => `/invoicedesigns/${id}`,
    delete: (id: number) => `/invoicedesigns/${id}`,
    setDefault: (id: number) => `/invoicedesigns/${id}/set-default`,
  },

  // Items Units
  itemsMultiBarcode: {
    list: '/itemsmultibarcode',
    get: (id: string) => `/itemsmultibarcode/${id}`,
    create: '/itemsmultibarcode',
    delete: (id: string) => `/itemsmultibarcode/${id}`,
    byItem: (itemId: string) => `/itemsmultibarcode?itemId=${itemId}`,
    byBarcode: (barcode: string) => `/itemsmultibarcode/barcode/${barcode}`,
  },

  itemsUnits: {
    list: '/itemsunits',
    get: (id: string) => `/itemsunits/${id}`,
    create: '/itemsunits',
    update: (id: string) => `/itemsunits/${id}`,
    delete: (id: string) => `/itemsunits/${id}`,
  },

  // Items Prices
  itemsPrices: {
    list: '/itemsprices',
    get: (id: string) => `/itemsprices/${id}`,
    create: '/itemsprices',
    update: (id: string) => `/itemsprices/${id}`,
    delete: (id: string) => `/itemsprices/${id}`,
  },

  // Modifiers (المعدلات - إضافات، بدون، إلخ)
  modifiers: {
    list: '/modifiers',
    get: (id: string) => `/modifiers/${id}`,
    create: '/modifiers',
    update: (id: string) => `/modifiers/${id}`,
    delete: (id: string) => `/modifiers/${id}`,
  },

  // Permissions & Menus
  menus: {
    list: '/menus',
    get: (id: string) => `/menus/${id}`,
    userMenus: (userId: string) => `/menus/user/${userId}`,
    parentMenus: (parentId: string) => `/menus/parent/${parentId}`,
  },
  roles: {
    list: '/roles',
    get: (id: string) => `/roles/${id}`,
    create: '/roles',
    update: (id: string) => `/roles/${id}`,
    delete: (id: string) => `/roles/${id}`,
    grantAllPermissions: (roleId?: string | number) => 
      roleId ? `/roles/${roleId}/grant-all-permissions` : '/roles/grant-all-permissions',
    // Keep old endpoint for backward compatibility
    grantAdminPermissions: '/roles/admin/grant-all-permissions',
  },
  userMenus: {
    list: '/mas_user_menu',
    get: (id: string) => `/mas_user_menu/${id}`,
    create: '/mas_user_menu',
    update: (id: string) => `/mas_user_menu/${id}`,
    delete: (id: string) => `/mas_user_menu/${id}`,
  },
  roleMenus: {
    list: '/mas_role_menu',
    get: (id: string) => `/mas_role_menu/${id}`,
    create: '/mas_role_menu',
    update: (id: string) => `/mas_role_menu/${id}`,
    delete: (id: string) => `/mas_role_menu/${id}`,
  },
  userRoles: {
    list: '/mas_user_role',
    get: (id: string) => `/mas_user_role/${id}`,
    create: '/mas_user_role',
    update: (id: string) => `/mas_user_role/${id}`,
    delete: (id: string) => `/mas_user_role/${id}`,
  },

  // App Versions
  appVersions: {
    check: '/appversions/check',
    latest: '/appversions/latest',
    current: '/appversions/current',
    list: '/appversions',
    get: (id: number) => `/appversions/${id}`,
    create: '/appversions',
    update: (id: number) => `/appversions/${id}`,
    delete: (id: number) => `/appversions/${id}`,
  },

  // Update Management
  update: {
    status: '/update/status',
    config: '/update/config',
    releaseNotes: (version: string) => `/update/release-notes/${version}`,
    progress: '/update/progress',
    download: '/update/download',
    backup: '/update/backup',
    install: '/update/install',
    rollback: '/update/rollback',
    backups: '/update/backups',
    deleteBackup: '/update/backups',
  },

  // System Settings
  systemSettings: {
    base: '/systemsettings',
    get: (key: string) => `/systemsettings/${key}`,
    create: '/systemsettings',
    update: (id: number) => `/systemsettings/${id}`,
    delete: (id: number) => `/systemsettings/${id}`,
    upsert: '/systemsettings/upsert',
    numberFormat: '/systemsettings/format/number',
    dateFormat: '/systemsettings/format/date',
  },

  zatcaIntegration: {
    generateCsr: '/zatcaintegration/generate-csr',
    getComplianceCsid: '/zatcaintegration/get-compliance-csid',
    generatePcsid: '/zatcaintegration/generate-pcsid',
    complianceTest: '/zatcaintegration/compliance-test',
    status: '/zatcaintegration/status',
  },

  documentNumbering: {
    base: '/documentnumbering',
    get: (id: string) => `/documentnumbering/${id}`,
    byType: (documentType: string) => `/documentnumbering/by-type/${documentType}`,
    preview: '/documentnumbering/preview',
    status: '/documentnumbering/status',
    update: (id: string) => `/documentnumbering/${id}`,
    delete: (id: string) => `/documentnumbering/${id}`,
  },

  // Serial Numbers
  serialNumbers: {
    base: '/serialnumbers',
    get: (id: string) => `/serialnumbers/${id}`,
    getByItem: (itemId: string) => `/serialnumbers/item/${itemId}`,
    getAvailable: (itemId: string) => `/serialnumbers/available/${itemId}`,
    getSoldForReturn: (itemId: string) => `/serialnumbers/sold-for-return/${itemId}`,
    getForPurchaseReturn: (itemId: string) => `/serialnumbers/for-purchase-return/${itemId}`,
    validate: '/serialnumbers/validate',
    create: '/serialnumbers/create',
    assign: '/serialnumbers/assign',
    return: '/serialnumbers/return',
    transfer: '/serialnumbers/transfer',
    damage: '/serialnumbers/damage',
    getMovements: (id: string) => `/serialnumbers/${id}/movements`,
    isEnabled: '/serialnumbers/settings/enabled',
  },

  // Item Custom Fields (Legacy - kept for backward compatibility)
  itemCustomFields: {
    list: '/itemcustomfields',
    active: '/itemcustomfields/active',
    itemCard: '/itemcustomfields/itemcard',
    invoice: '/itemcustomfields/invoice',
    get: (id: number) => `/itemcustomfields/${id}`,
    create: '/itemcustomfields',
    update: (id: number) => `/itemcustomfields/${id}`,
    delete: (id: number) => `/itemcustomfields/${id}`,
  },

  // Item Custom Field Values (Legacy - kept for backward compatibility)
  itemCustomFieldValues: {
    getByItem: (itemId: string) => `/itemcustomfieldvalues/item/${itemId}`,
    getByItemAsDict: (itemId: string) => `/itemcustomfieldvalues/item/${itemId}/dictionary`,
    create: '/itemcustomfieldvalues',
    bulk: '/itemcustomfieldvalues/bulk',
    delete: (id: number) => `/itemcustomfieldvalues/${id}`,
    deleteByItem: (itemId: string) => `/itemcustomfieldvalues/item/${itemId}`,
  },

  // Form Custom Fields (General system for all forms)
  formCustomFields: {
    list: (formType: string) => `/formcustomfields?formType=${formType}`,
    get: (id: number) => `/formcustomfields/${id}`,
    create: '/formcustomfields',
    update: (id: number) => `/formcustomfields/${id}`,
    delete: (id: number) => `/formcustomfields/${id}`,
  },

  // Form Custom Field Values
  formCustomFieldValues: {
    list: (formType: string, entityId: string) => `/formcustomfieldvalues?formType=${formType}&entityId=${entityId}`,
    getAsDict: (formType: string, entityId: string) => `/formcustomfieldvalues/dict?formType=${formType}&entityId=${entityId}`,
    create: '/formcustomfieldvalues',
    update: '/formcustomfieldvalues',
    upsert: '/formcustomfieldvalues/upsert',
    bulk: (formType: string, entityId: string) => `/formcustomfieldvalues/bulk?formType=${formType}&entityId=${entityId}`,
    delete: (formType: string, entityId: string, fieldKey: string) => `/formcustomfieldvalues?formType=${formType}&entityId=${entityId}&fieldKey=${fieldKey}`,
  },

  // Action Permissions
  actionPermissions: {
    check: '/actionpermissions/check',
    getUserPermissions: (userId: string) => `/actionpermissions/user/${userId}`,
    getRolePermissions: (roleId: string) => `/actionpermissions/role/${roleId}`,
    create: '/actionpermissions',
    update: (id: number) => `/actionpermissions/${id}`,
    delete: (id: number) => `/actionpermissions/${id}`,
    bulkSave: '/actionpermissions/bulk-save',
  },

  permissionEngine: {
    evaluate: '/permission-engine/evaluate',
    effective: (userId?: string | number) =>
      userId ? `/permission-engine/effective?userId=${userId}` : '/permission-engine/effective',
    actions: '/permission-engine/catalog/actions',
    saveAction: '/permission-engine/catalog/actions',
    deleteAction: (id: number) => `/permission-engine/catalog/actions/${id}`,
    resources: '/permission-engine/catalog/resources',
    saveResource: '/permission-engine/catalog/resources',
    packs: '/permission-engine/packs',
    savePack: '/permission-engine/packs',
    assignPack: '/permission-engine/packs/assign',
    inheritance: '/permission-engine/inheritance',
    setInheritance: '/permission-engine/inheritance',
    removeInheritance: (id: number) => `/permission-engine/inheritance/${id}`,
    grants: '/permission-engine/grants',
    revokeGrant: (id: number) => `/permission-engine/grants/${id}`,
    thresholds: '/permission-engine/thresholds',
    dataScopes: '/permission-engine/data-scopes',
    audit: '/permission-engine/audit',
  },

  // Activity Logs
  activityLogs: {
    create: '/activitylog',
    list: '/activitylog',
    get: (id: number) => `/activitylog/${id}`,
    byUser: (userId: string | number) => `/activitylog/user/${userId}`,
    byEntity: (entityType: string, entityId: string) => `/activitylog/entity/${entityType}/${entityId}`,
    failed: '/activitylog/failed',
  },

  auditLogs: {
    list: '/activitylog',
    get: (id: number) => `/activitylog/${id}`,
  },

  // Audit Trail
  auditTrail: {
    list: '/audittrail',
    getByEntity: (entityType: string, entityId: string) => `/audittrail/entity/${entityType}/${entityId}`,
  },

  // License
  license: {
    list: '/license',
    get: (id: number) => `/license/${id}`,
    create: '/license',
    update: (id: number) => `/license/${id}`,
    delete: (id: number) => `/license/${id}`,
    active: '/license/active',
    check: '/license/check',
    registerDeviceTrial: '/license/register-device-trial',
    deviceCode: '/license/device-code',
    activateCode: '/license/activate-code',
    updateLocation: '/license/update-location',
    extend: (id: number) => `/license/${id}/extend`,
    updateExpired: '/license/update-expired',
    saveJson: '/license/save-json',
    getJson: '/license/get-json',
    updateFromEncrypted: (id: number) => `/license/${id}/update-from-encrypted`,
  },

  // Device Registration
  deviceRegistration: {
    register: '/deviceregistration/register',
    check: (deviceId: string) => `/deviceregistration/check/${deviceId}`,
    updateLastSeen: (deviceId: string) => `/deviceregistration/update-last-seen/${deviceId}`,
    presence: '/deviceregistration/presence',
    list: '/deviceregistration',
    block: (id: number) => `/deviceregistration/${id}/block`,
    unblock: (id: number) => `/deviceregistration/${id}/unblock`,
  },

  tailscale: {
    devices: '/tailscale/devices',
    settings: '/tailscale/settings',
  },

  // Signals
  signals: {
    poll: '/registry/poll',
    updateStatus: '/registry/signals/status',
    get: (signalId: string) => `/registry/signals/${signalId}`,
    getByDevice: (deviceId: string) => `/registry/clients/${deviceId}/signals`,
    create: '/registry/signals',
    getAllClients: '/registry/clients',
    getClient: (clientId: string) => `/registry/clients/${clientId}`,
    registerClient: '/registry/register',
  },

  screenView: {
    status: '/screen-view/status',
    pending: '/screen-view/pending',
    requests: '/screen-view/requests',
    request: (id: string) => `/screen-view/requests/${id}`,
    approve: (id: string) => `/screen-view/requests/${id}/approve`,
    reject: (id: string) => `/screen-view/requests/${id}/reject`,
    cancel: (id: string) => `/screen-view/requests/${id}/cancel`,
    session: (id: string) => `/screen-view/sessions/${id}`,
    endSession: (id: string) => `/screen-view/sessions/${id}/end`,
  },

  // Restore Points
  restorePoints: {
    list: '/restorepoints',
    get: (id: number) => `/restorepoints/${id}`,
    create: '/restorepoints',
    update: (id: number) => `/restorepoints/${id}`,
    delete: (id: number) => `/restorepoints/${id}`,
    latest: '/restorepoints/latest',
    system: '/restorepoints/system',
    markRestored: (id: number) => `/restorepoints/${id}/mark-restored`,
  },

  // Database Backup
  databaseBackup: {
    create: '/databasebackup/backup',
    list: '/databasebackup/backups',
    download: (fileName: string) => `/databasebackup/download/${encodeURIComponent(fileName)}`,
    delete: (fileName: string) => `/databasebackup/backups/${encodeURIComponent(fileName)}`,
    restore: (fileName: string) => `/databasebackup/restore/${encodeURIComponent(fileName)}`,
  },

  // External Audit Links
  externalAuditLinks: {
    list: '/externalauditlinks',
    get: (id: number) => `/externalauditlinks/${id}`,
    create: '/externalauditlinks',
    update: (id: number) => `/externalauditlinks/${id}`,
    delete: (id: number) => `/externalauditlinks/${id}`,
    deactivate: (id: number) => `/externalauditlinks/${id}/deactivate`,
    accessLogs: (id: number) => `/externalauditlinks/${id}/access-logs`,
  },

  // Invoice Scan
  invoiceScan: {
    scan: '/invoicescan/scan',
    history: '/invoicescan/history',
    get: (id: string) => `/invoicescan/${id}`,
  },

  // Loyalty
  loyalty: {
    list: '/loyalty',
    get: (id: string) => `/loyalty/${id}`,
    customerLoyalty: (contactId: string) => `/loyalty/customer/${contactId}`,
    updatePoints: '/loyalty/update-points',
    wallet: (contactId: string) => `/loyalty/wallet/${contactId}`,
    // Program
    program: {
      active: (companyId?: string) => companyId ? `/loyalty/program/active?companyId=${companyId}` : '/loyalty/program/active',
      create: '/loyalty/program',
      update: (id: string) => `/loyalty/program/${id}`,
    },
    // Points
    points: {
      getAll: (companyId?: string) => companyId ? `/loyalty/points?companyId=${companyId}` : '/loyalty/points',
      get: (contactId: string, companyId?: string) => companyId ? `/loyalty/points/${contactId}?companyId=${companyId}` : `/loyalty/points/${contactId}`,
      enroll: (contactId: string, companyId?: string) => companyId ? `/loyalty/points/enroll?contactId=${contactId}&companyId=${companyId}` : `/loyalty/points/enroll?contactId=${contactId}`,
      earn: '/loyalty/points/earn',
      redeem: '/loyalty/points/redeem',
      adjust: '/loyalty/points/adjust',
    },
    // Transactions
    transactions: {
      byContact: (contactId: string, companyId?: string) => companyId ? `/loyalty/transactions/${contactId}?companyId=${companyId}` : `/loyalty/transactions/${contactId}`,
      all: (companyId?: string) => companyId ? `/loyalty/transactions?companyId=${companyId}` : '/loyalty/transactions',
    },
    // Statistics
    statistics: {
      get: (companyId?: string) => companyId ? `/loyalty/statistics?companyId=${companyId}` : '/loyalty/statistics',
      topCustomers: (companyId?: string, topCount?: number) => {
        const params = new URLSearchParams()
        if (companyId) params.append('companyId', companyId)
        if (topCount) params.append('topCount', topCount.toString())
        return `/loyalty/top-customers?${params.toString()}`
      },
    },
    // Expiration
    expiration: {
      process: (companyId?: string) => companyId ? `/loyalty/process-expiration?companyId=${companyId}` : '/loyalty/process-expiration',
    },
  },

  // App Configuration
  appConfiguration: {
    list: '/appconfiguration',
    get: (key: string) => `/appconfiguration/${key}`,
    create: '/appconfiguration',
    update: (key: string) => `/appconfiguration/${key}`,
    delete: (key: string) => `/appconfiguration/${key}`,
    history: (key: string) => `/appconfiguration/${key}/history`,
  },

  // Lot Tracking
  lotTracking: {
    list: '/lottracking',
    get: (id: string) => `/lottracking/${id}`,
    available: (itemId: string) => `/lottracking/available/${itemId}`,
    expiring: '/lottracking/expiring',
  },

  // Medicine Tracking
  medicineTracking: {
    list: '/medicinetracking',
    get: (id: string) => `/medicinetracking/${id}`,
    byQRCode: (qrCode: string) => `/medicinetracking/qrcode/${encodeURIComponent(qrCode)}`,
    parseQR: '/medicinetracking/parse-qr',
    create: '/medicinetracking',
    update: (id: string) => `/medicinetracking/${id}`,
    delete: (id: string) => `/medicinetracking/${id}`,
    available: (itemId: string, storeId?: string, requiredQuantity?: number, includeExpired?: boolean) => {
      const params = new URLSearchParams({ itemId })
      if (storeId) params.append('storeId', storeId)
      if (requiredQuantity !== undefined) params.append('requiredQuantity', requiredQuantity.toString())
      if (includeExpired) params.append('includeExpired', 'true')
      return `/medicinetracking/available/${itemId}?${params.toString()}`
    },
    expiring: (days?: number, itemId?: string, storeId?: string) => {
      const params = new URLSearchParams()
      if (days) params.append('days', days.toString())
      if (itemId) params.append('itemId', itemId)
      if (storeId) params.append('storeId', storeId)
      return `/medicinetracking/expiring?${params.toString()}`
    },
    expired: (itemId?: string, storeId?: string) => {
      const params = new URLSearchParams()
      if (itemId) params.append('itemId', itemId)
      if (storeId) params.append('storeId', storeId)
      return `/medicinetracking/expired?${params.toString()}`
    },
    reportMedicineDetails: (params?: Record<string, string | number | boolean | undefined>) => {
      const search = new URLSearchParams()
      Object.entries(params ?? {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') search.append(key, String(value))
      })
      const query = search.toString()
      return `/medicinetracking/report/medicine-details${query ? `?${query}` : ''}`
    },
    reportAvailableQuantities: (params?: Record<string, string | number | boolean | undefined>) => {
      const search = new URLSearchParams()
      Object.entries(params ?? {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') search.append(key, String(value))
      })
      const query = search.toString()
      return `/medicinetracking/report/available-quantities${query ? `?${query}` : ''}`
    },
  },

  // Promotions
  promotions: {
    list: (companyId?: string, branchId?: string) => {
      const params = new URLSearchParams()
      if (companyId) params.append('companyId', companyId)
      if (branchId) params.append('branchId', branchId)
      const queryString = params.toString()
      return queryString ? `/promotion?${queryString}` : '/promotion'
    },
    active: (companyId?: string, branchId?: string) => {
      const params = new URLSearchParams()
      if (companyId) params.append('companyId', companyId)
      if (branchId) params.append('branchId', branchId)
      return `/promotion/active?${params.toString()}`
    },
    get: (id: string) => `/promotion/${id}`,
    create: '/promotion',
    update: (id: string) => `/promotion/${id}`,
    delete: (id: string) => `/promotion/${id}`,
    apply: '/promotion/apply',
    applyCoupon: '/promotion/apply-coupon',
    coupon: {
      get: (code: string) => `/promotion/coupon/${code}`,
      list: (contactId: string) => `/promotion/coupons/contact/${contactId}`,
      create: '/promotion/coupon',
    },
    usage: {
      record: '/promotion/usage',
      list: (promotionId?: string, contactId?: string, companyId?: string) => {
        const params = new URLSearchParams()
        if (promotionId) params.append('promotionId', promotionId)
        if (contactId) params.append('contactId', contactId)
        if (companyId) params.append('companyId', companyId)
        return `/promotion/usage?${params.toString()}`
      },
    },
    statistics: (companyId?: string) => {
      const params = new URLSearchParams()
      if (companyId) params.append('companyId', companyId)
      return `/promotion/statistics?${params.toString()}`
    },
    top: (companyId?: string, topCount?: number) => {
      const params = new URLSearchParams()
      if (companyId) params.append('companyId', companyId)
      if (topCount) params.append('topCount', topCount.toString())
      return `/promotion/top?${params.toString()}`
    },
  },  // Fixed Assets
  fixedAssets: {
    list: '/fixedassets',
    get: (id: string) => `/fixedassets/${id}`,
    create: '/fixedassets',
    update: (id: string) => `/fixedassets/${id}`,
    delete: (id: string) => `/fixedassets/${id}`,
    calculateDepreciation: (id: string) => `/fixedassets/${id}/depreciations/calculate`,
    calculateDepreciationForPeriod: '/fixedassets/depreciations/calculate-period',
    getDepreciations: (id: string) => `/fixedassets/${id}/depreciations`,
    getAdjustments: (id: string) => `/fixedassets/${id}/adjustments`,
    createAdjustment: (id: string) => `/fixedassets/${id}/adjustments`,
    getDisposals: (id: string) => `/fixedassets/${id}/disposals`,
    createDisposal: (id: string) => `/fixedassets/${id}/disposals`,
    listDisposals: '/fixedassets/disposals',
    capitalMaintenances: {
      list: '/fixedassets/capital-maintenances',
      create: '/fixedassets/capital-maintenances',
      delete: (id: string) => `/fixedassets/capital-maintenances/${id}`,
    },
    routineMaintenances: {
      list: '/fixedassets/routine-maintenances',
      create: '/fixedassets/routine-maintenances',
      delete: (id: string) => `/fixedassets/routine-maintenances/${id}`,
    },
    getReport: (id: string) => `/fixedassets/${id}/report`,
    getSummaryReport: '/fixedassets/reports/summary',
    getRoutineExpensesReport: '/fixedassets/reports/routine-expenses',
    accountConfigStatus: '/fixedassets/account-config-status',
  },

  // Production System
  production: {
    boms: {
      list: (branchId?: string, itemId?: string, searchTerm?: string, approvalStatus?: string) => {
        const params = new URLSearchParams()
        if (branchId) params.append('branchId', branchId)
        if (itemId) params.append('itemId', itemId)
        if (searchTerm) params.append('searchTerm', searchTerm)
        if (approvalStatus) params.append('approvalStatus', approvalStatus)
        const queryString = params.toString()
        return queryString ? `/production/boms?${queryString}` : '/production/boms'
      },
      get: (id: string) => `/production/boms/${id}`,
      create: '/production/boms',
      update: (id: string) => `/production/boms/${id}`,
      delete: (id: string) => `/production/boms/${id}`,
      default: (itemId: string) => `/production/boms/default/${itemId}`,
      submitReview: (id: string) => `/production/boms/${id}/submit-review`,
      approve: (id: string) => `/production/boms/${id}/approve`,
      activate: (id: string) => `/production/boms/${id}/activate`,
      archive: (id: string) => `/production/boms/${id}/archive`,
      newVersion: (id: string) => `/production/boms/${id}/new-version`,
    },
    wasteTypes: {
      list: '/production/waste-types',
      updateAccount: (id: string) => `/production/waste-types/${id}/account`,
    },
    orders: {
      list: (branchId?: string, status?: string, searchTerm?: string) => {
        const params = new URLSearchParams()
        if (branchId) params.append('branchId', branchId)
        if (status) params.append('status', status)
        if (searchTerm) params.append('searchTerm', searchTerm)
        const queryString = params.toString()
        return queryString ? `/production/orders?${queryString}` : '/production/orders'
      },
      get: (id: string) => `/production/orders/${id}`,
      create: '/production/orders',
      update: (id: string) => `/production/orders/${id}`,
      delete: (id: string) => `/production/orders/${id}`,
      issueMaterials: (id: string) => `/production/orders/${id}/issue-materials`,
      receiveProduct: (id: string) => `/production/orders/${id}/receive-product`,
      complete: (id: string) => `/production/orders/${id}/complete`,
      cancel: (id: string) => `/production/orders/${id}/cancel`,
    },
    costCalculations: {
      list: (params?: { page?: number; pageSize?: number; search?: string; status?: string; mode?: string }) => {
        const query = new URLSearchParams()
        if (params?.page) query.append('page', String(params.page))
        if (params?.pageSize) query.append('pageSize', String(params.pageSize))
        if (params?.search) query.append('search', params.search)
        if (params?.status) query.append('status', params.status)
        if (params?.mode) query.append('mode', params.mode)
        const text = query.toString()
        return text ? `/production/cost-calculations?${text}` : '/production/cost-calculations'
      },
      get: (id: string) => `/production/cost-calculations/${id}`,
      create: '/production/cost-calculations',
      update: (id: string) => `/production/cost-calculations/${id}`,
      approve: (id: string) => `/production/cost-calculations/${id}/approve`,
      cancel: (id: string) => `/production/cost-calculations/${id}/cancel`,
      itemCost: (itemId: string, unitId?: string, storeId?: string) => {
        const query = new URLSearchParams({ itemId })
        if (unitId) query.append('unitId', unitId)
        if (storeId) query.append('storeId', storeId)
        return `/production/cost-calculations/item-cost?${query.toString()}`
      },
      fromOrder: (orderId: string) => `/production/cost-calculations/from-order/${orderId}`,
    },
  },

  // Car Rental
  carRental: {
    cars: {
      list: (branchId?: string, status?: string) => {
        const params = new URLSearchParams()
        if (branchId) params.append('branchId', branchId)
        if (status) params.append('status', status)
        const query = params.toString()
        return query ? `/carrental/cars?${query}` : '/carrental/cars'
      },
      get: (id: number | string) => `/carrental/cars/${id}`,
      create: '/carrental/cars',
      update: (id: number | string) => `/carrental/cars/${id}`,
      delete: (id: number | string) => `/carrental/cars/${id}`,
      availability: (id: number | string) => `/carrental/cars/${id}/availability`,
    },
    carTypes: {
      list: '/carrental/cartypes',
      get: (id: number | string) => `/carrental/cartypes/${id}`,
      create: '/carrental/cartypes',
      update: (id: number | string) => `/carrental/cartypes/${id}`,
      delete: (id: number | string) => `/carrental/cartypes/${id}`,
    },
    contracts: {
      list: (branchId?: string, status?: string) => {
        const params = new URLSearchParams()
        if (branchId) params.append('branchId', branchId)
        if (status) params.append('status', status)
        const query = params.toString()
        return query ? `/carrental/contracts?${query}` : '/carrental/contracts'
      },
      get: (id: number | string) => `/carrental/contracts/${id}`,
      create: '/carrental/contracts',
      update: (id: number | string) => `/carrental/contracts/${id}`,
      delete: (id: number | string) => `/carrental/contracts/${id}`,
      nextNumber: (branchId: string) => `/carrental/contracts/next-number/${branchId}`,
    },
    payments: {
      list: '/carrental/payments',
      create: '/carrental/payments',
      byContract: (contractId: number | string) => `/carrental/payments/contract/${contractId}`,
    },
    dashboard: {
      summary: '/carrental/dashboard/summary',
      availableCars: '/carrental/dashboard/available-cars',
    }
  },

  // Custom Forms
  customForms: {
    list: '/customforms',
    menu: '/customforms/menu',
    get: (id: number) => `/customforms/${id}`,
    create: '/customforms',
    update: (id: number) => `/customforms/${id}`,
    delete: (id: number) => `/customforms/${id}`,
    submit: (id: number) => `/customforms/${id}/submit`,
    submissions: (id: number, fromDate?: string, toDate?: string, status?: string, submittedById?: number) => {
      const params = new URLSearchParams()
      if (fromDate) params.append('fromDate', fromDate)
      if (toDate) params.append('toDate', toDate)
      if (status) params.append('status', status)
      if (submittedById) params.append('submittedById', submittedById.toString())
      const queryString = params.toString()
      return `/customforms/${id}/submissions${queryString ? `?${queryString}` : ''}`
    },
    getSubmission: (formId: number, submissionId: number) => `/customforms/${formId}/submissions/${submissionId}`,
    updateSubmission: (formId: number, submissionId: number) => `/customforms/${formId}/submissions/${submissionId}`,
    deleteSubmission: (formId: number, submissionId: number) => `/customforms/${formId}/submissions/${submissionId}`,
    report: '/customforms/report',
  },
  
  // Municipality Module
  municipalityFinanceExpenses: {
    list: '/municipalityfinanceexpenses',
    get: (id: string) => `/municipalityfinanceexpenses/${id}`,
    create: '/municipalityfinanceexpenses',
    update: (id: string) => `/municipalityfinanceexpenses/${id}`,
    delete: (id: string) => `/municipalityfinanceexpenses/${id}`,
  },
  municipalityFinanceExpenseTypes: {
    list: '/municipalityfinanceexpensetypes',
    get: (id: string) => `/municipalityfinanceexpensetypes/${id}`,
    create: '/municipalityfinanceexpensetypes',
    update: (id: string) => `/municipalityfinanceexpensetypes/${id}`,
    delete: (id: string) => `/municipalityfinanceexpensetypes/${id}`,
  },
  municipalityRevenues: {
    list: '/municipalityrevenues',
    get: (id: string) => `/municipalityrevenues/${id}`,
    getBySerial: (serial: string) => `/municipalityrevenues/by-serial/${encodeURIComponent(serial)}`,
    create: '/municipalityrevenues',
    update: (id: string) => `/municipalityrevenues/${id}`,
    delete: (id: string) => `/municipalityrevenues/${id}`,
    post: (id: string) => `/municipalityrevenues/${id}/post`,
    postBySerial: () => '/municipalityrevenues/post-by-serial',
    bulk: '/municipalityrevenues/bulk',
    bulkAll: '/municipalityrevenues/bulk/all',
    bulkPost: '/municipalityrevenues/bulk-post',
    neighborhoodSummary: '/municipalityrevenues/neighborhood-summary',
  },

  // Debt Recovery
  debtRecovery: {
    overdueInvoices: '/debt-recovery/overdue-invoices',
    riskScoring: '/debt-recovery/risk-scoring',
    sendReminder: '/debt-recovery/send-reminder',
    summary: '/debt-recovery/summary',
  },
  municipalityBillingItems: {
    list: '/municipalitybillingitems',
    get: (id: string) => `/municipalitybillingitems/${id}`,
    create: '/municipalitybillingitems',
    update: (id: string) => `/municipalitybillingitems/${id}`,
    delete: (id: string) => `/municipalitybillingitems/${id}`,
    bulk: '/municipalitybillingitems/bulk',
    pay: (id: string) => `/municipalitybillingitems/${id}/pay`,
  },
  municipalityBillingPayments: {
    list: '/municipalitybillingpayments',
    get: (id: string) => `/municipalitybillingpayments/${id}`,
  },
  municipalityCitizens: {
    list: '/municipalitycitizens',
  },
  municipalityRevenueTypes: {
    list: '/municipalityrevenuetypes',
    get: (id: string) => `/municipalityrevenuetypes/${id}`,
    create: '/municipalityrevenuetypes',
    update: (id: string) => `/municipalityrevenuetypes/${id}`,
    delete: (id: string) => `/municipalityrevenuetypes/${id}`,
  },
  municipalityLicenses: {
    list: '/municipalitylicenses',
    get: (id: string) => `/municipalitylicenses/${id}`,
    create: '/municipalitylicenses',
    update: (id: string) => `/municipalitylicenses/${id}`,
    delete: (id: string) => `/municipalitylicenses/${id}`,
    post: (id: string) => `/municipalitylicenses/${id}/post`,
    renew: (id: string) => `/municipalitylicenses/${id}/renew`,
  },

  municipalityLicenseTypes: {
    list: '/municipalitylicensetypes',
    get: (id: string) => `/municipalitylicensetypes/${id}`,
    create: '/municipalitylicensetypes',
    update: (id: string) => `/municipalitylicensetypes/${id}`,
    delete: (id: string) => `/municipalitylicensetypes/${id}`,
  },
  municipalitySalaries: {
    list: '/municipalitysalaries',
    get: (id: string) => `/municipalitysalaries/${id}`,
    create: '/municipalitysalaries',
    update: (id: string) => `/municipalitysalaries/${id}`,
    delete: (id: string) => `/municipalitysalaries/${id}`,
    post: (id: string) => `/municipalitysalaries/${id}/post`,
  },
  municipalitySalaryTypes: {
    list: '/municipalitysalarytypes',
    get: (id: string) => `/municipalitysalarytypes/${id}`,
    create: '/municipalitysalarytypes',
    update: (id: string) => `/municipalitysalarytypes/${id}`,
    delete: (id: string) => `/municipalitysalarytypes/${id}`,
  },
  municipalityServiceTypes: {
    list: '/municipalityservicetypes',
    get: (id: string) => `/municipalityservicetypes/${id}`,
    create: '/municipalityservicetypes',
    update: (id: string) => `/municipalityservicetypes/${id}`,
    delete: (id: string) => `/municipalityservicetypes/${id}`,
  },
  municipalityServices: {
    list: '/municipalityservices',
    get: (id: string) => `/municipalityservices/${id}`,
    create: '/municipalityservices',
    update: (id: string) => `/municipalityservices/${id}`,
    delete: (id: string) => `/municipalityservices/${id}`,
  },
  municipalityHousingUnits: {
    list: '/municipalityhousingunits',
    get: (id: string) => `/municipalityhousingunits/${id}`,
    create: '/municipalityhousingunits',
    update: (id: string) => `/municipalityhousingunits/${id}`,
    delete: (id: string) => `/municipalityhousingunits/${id}`,
  },
  municipalityHousingUnitTypes: {
    list: '/municipalityhousingunittypes',
    get: (id: string) => `/municipalityhousingunittypes/${id}`,
    create: '/municipalityhousingunittypes',
    update: (id: string) => `/municipalityhousingunittypes/${id}`,
    delete: (id: string) => `/municipalityhousingunittypes/${id}`,
  },
  municipalityHousingUnitUsageTypes: {
    list: '/municipalityhousingunitusagetypes',
    get: (id: string) => `/municipalityhousingunitusagetypes/${id}`,
    create: '/municipalityhousingunitusagetypes',
    update: (id: string) => `/municipalityhousingunitusagetypes/${id}`,
    delete: (id: string) => `/municipalityhousingunitusagetypes/${id}`,
  },
  municipalityHousingUnitRelations: {
    list: '/municipalityhousingunitrelations',
    get: (id: string) => `/municipalityhousingunitrelations/${id}`,
    create: '/municipalityhousingunitrelations',
    update: (id: string) => `/municipalityhousingunitrelations/${id}`,
    delete: (id: string) => `/municipalityhousingunitrelations/${id}`,
  },
  municipalityServiceRequests: {
    list: '/municipalityservicerequests',
    get: (id: string) => `/municipalityservicerequests/${id}`,
    create: '/municipalityservicerequests',
    update: (id: string) => `/municipalityservicerequests/${id}`,
    delete: (id: string) => `/municipalityservicerequests/${id}`,
  },
  municipalityComplaintTypes: {
    list: '/municipalitycomplainttypes',
    get: (id: string) => `/municipalitycomplainttypes/${id}`,
    create: '/municipalitycomplainttypes',
    update: (id: string) => `/municipalitycomplainttypes/${id}`,
    delete: (id: string) => `/municipalitycomplainttypes/${id}`,
  },
  municipalityDepartments: {
    list: '/municipalitydepartments',
    get: (id: string) => `/municipalitydepartments/${id}`,
    create: '/municipalitydepartments',
    update: (id: string) => `/municipalitydepartments/${id}`,
    delete: (id: string) => `/municipalitydepartments/${id}`,
  },
  municipalityCouncilMembers: {
    list: '/municipalitycouncilmembers',
    get: (id: string) => `/municipalitycouncilmembers/${id}`,
    create: '/municipalitycouncilmembers',
    update: (id: string) => `/municipalitycouncilmembers/${id}`,
    delete: (id: string) => `/municipalitycouncilmembers/${id}`,
  },
  municipalityCommittees: {
    list: '/municipalitycommittees',
    get: (id: string) => `/municipalitycommittees/${id}`,
    create: '/municipalitycommittees',
    update: (id: string) => `/municipalitycommittees/${id}`,
    delete: (id: string) => `/municipalitycommittees/${id}`,
  },
  municipalityCommitteeMembers: {
    list: '/municipalitycommitteemembers',
    get: (id: string) => `/municipalitycommitteemembers/${id}`,
    create: '/municipalitycommitteemembers',
    update: (id: string) => `/municipalitycommitteemembers/${id}`,
    delete: (id: string) => `/municipalitycommitteemembers/${id}`,
  },
  municipalityCommitteeTasks: {
    list: '/municipalitycommitteetasks',
    get: (id: string) => `/municipalitycommitteetasks/${id}`,
    create: '/municipalitycommitteetasks',
    update: (id: string) => `/municipalitycommitteetasks/${id}`,
    delete: (id: string) => `/municipalitycommitteetasks/${id}`,
  },
  municipalityCommitteeMeetings: {
    list: '/municipalitycommitteemeetings',
    get: (id: string) => `/municipalitycommitteemeetings/${id}`,
    create: '/municipalitycommitteemeetings',
    update: (id: string) => `/municipalitycommitteemeetings/${id}`,
    delete: (id: string) => `/municipalitycommitteemeetings/${id}`,
  },
  municipalityCommitteeReports: {
    list: '/municipalitycommitteereports',
    get: (id: string) => `/municipalitycommitteereports/${id}`,
    create: '/municipalitycommitteereports',
    update: (id: string) => `/municipalitycommitteereports/${id}`,
    delete: (id: string) => `/municipalitycommitteereports/${id}`,
  },
  municipalityEmployees: {
    list: '/municipalityemployees',
    get: (id: string) => `/municipalityemployees/${id}`,
    create: '/municipalityemployees',
    update: (id: string) => `/municipalityemployees/${id}`,
    delete: (id: string) => `/municipalityemployees/${id}`,
  },
  municipalityEmployeeSalaryProfiles: {
    list: '/municipalityemployeesalaryprofiles',
    get: (id: string) => `/municipalityemployeesalaryprofiles/${id}`,
    create: '/municipalityemployeesalaryprofiles',
    update: (id: string) => `/municipalityemployeesalaryprofiles/${id}`,
    delete: (id: string) => `/municipalityemployeesalaryprofiles/${id}`,
  },
  municipalityEmployeeSalaryChanges: {
    list: '/municipalityemployeesalarychanges',
    get: (id: string) => `/municipalityemployeesalarychanges/${id}`,
  },
  municipalityAchievements: {
    list: '/municipalityachievements',
    get: (id: string) => `/municipalityachievements/${id}`,
    create: '/municipalityachievements',
    update: (id: string) => `/municipalityachievements/${id}`,
    delete: (id: string) => `/municipalityachievements/${id}`,
  },
  municipalityAchievementAttachments: {
    list: '/municipalityachievementattachments',
    get: (id: string) => `/municipalityachievementattachments/${id}`,
    create: '/municipalityachievementattachments',
    delete: (id: string) => `/municipalityachievementattachments/${id}`,
  },
  municipalityCouncilPresidentElections: {
    list: '/municipalitycouncilpresidentelections',
    get: (id: string) => `/municipalitycouncilpresidentelections/${id}`,
    create: '/municipalitycouncilpresidentelections',
    openNominations: (id: string) => `/municipalitycouncilpresidentelections/${id}/open-nominations`,
    close: (id: string) => `/municipalitycouncilpresidentelections/${id}/close`,
    candidates: (id: string) => `/municipalitycouncilpresidentelections/${id}/candidates`,
    updateCandidate: (candidateId: string) => `/municipalitycouncilpresidentelections/candidates/${candidateId}`,
  },
  municipalityComplaints: {
    list: '/municipalitycomplaints',
    get: (id: string) => `/municipalitycomplaints/${id}`,
    create: '/municipalitycomplaints',
    update: (id: string) => `/municipalitycomplaints/${id}`,
    close: (id: string) => `/municipalitycomplaints/${id}/close`,
    timeline: (id: string) => `/municipalitycomplaints/${id}/timeline`,
  },
  municipalityProjectTypes: {
    list: '/municipalityprojecttypes',
    get: (id: string) => `/municipalityprojecttypes/${id}`,
    create: '/municipalityprojecttypes',
    update: (id: string) => `/municipalityprojecttypes/${id}`,
    delete: (id: string) => `/municipalityprojecttypes/${id}`,
  },
  municipalityProjectRequests: {
    list: '/municipalityprojectrequests',
    get: (id: string) => `/municipalityprojectrequests/${id}`,
    create: '/municipalityprojectrequests',
    update: (id: string) => `/municipalityprojectrequests/${id}`,
    delete: (id: string) => `/municipalityprojectrequests/${id}`,
  },
  municipalityProjects: {
    list: '/municipalityprojects',
    get: (id: string) => `/municipalityprojects/${id}`,
    create: '/municipalityprojects',
    update: (id: string) => `/municipalityprojects/${id}`,
    close: (id: string) => `/municipalityprojects/${id}/close`,
    timeline: (id: string) => `/municipalityprojects/${id}/timeline`,
  },
  municipalityProjectTasks: {
    list: '/municipalityprojecttasks',
    get: (id: string) => `/municipalityprojecttasks/${id}`,
    create: '/municipalityprojecttasks',
    update: (id: string) => `/municipalityprojecttasks/${id}`,
    delete: (id: string) => `/municipalityprojecttasks/${id}`,
  },
  municipalityProjectExpenses: {
    list: '/municipalityprojectexpenses',
    get: (id: string) => `/municipalityprojectexpenses/${id}`,
    create: '/municipalityprojectexpenses',
    update: (id: string) => `/municipalityprojectexpenses/${id}`,
    delete: (id: string) => `/municipalityprojectexpenses/${id}`,
  },
  municipalityAssets: {
    list: '/municipalityassets',
    get: (id: string) => `/municipalityassets/${id}`,
    create: '/municipalityassets',
    update: (id: string) => `/municipalityassets/${id}`,
    close: (id: string) => `/municipalityassets/${id}/close`,
  },
  municipalityAssetCategories: {
    list: '/municipalityassetcategories',
    get: (id: string) => `/municipalityassetcategories/${id}`,
    create: '/municipalityassetcategories',
    update: (id: string) => `/municipalityassetcategories/${id}`,
    delete: (id: string) => `/municipalityassetcategories/${id}`,
  },
  municipalityBudgets: {
    list: '/municipalitybudgets',
    get: (id: string) => `/municipalitybudgets/${id}`,
    create: '/municipalitybudgets',
    update: (id: string) => `/municipalitybudgets/${id}`,
    delete: (id: string) => `/municipalitybudgets/${id}`,
  },
  municipalityBudgetLines: {
    list: '/municipalitybudgetlines',
    get: (id: string) => `/municipalitybudgetlines/${id}`,
    create: '/municipalitybudgetlines',
    update: (id: string) => `/municipalitybudgetlines/${id}`,
    delete: (id: string) => `/municipalitybudgetlines/${id}`,
  },
  municipalityBudgetTransfers: {
    list: '/municipalitybudgettransfers',
    get: (id: string) => `/municipalitybudgettransfers/${id}`,
    create: '/municipalitybudgettransfers',
  },
  municipalityBudgetTransactions: {
    list: '/municipalitybudgettransactions',
    create: '/municipalitybudgettransactions',
  },
  municipalityOfficialDocuments: {
    list: '/municipalityofficialdocuments',
    get: (id: string) => `/municipalityofficialdocuments/${id}`,
    create: '/municipalityofficialdocuments',
    update: (id: string) => `/municipalityofficialdocuments/${id}`,
    issue: (id: string) => `/municipalityofficialdocuments/${id}/issue`,
    cancel: (id: string) => `/municipalityofficialdocuments/${id}/cancel`,
    renew: (id: string) => `/municipalityofficialdocuments/${id}/renew`,
    statusLog: (id: string) => `/municipalityofficialdocuments/${id}/status-log`,
  },
  municipalityOfficialDocumentTypes: {
    list: '/municipalityofficialdocumenttypes',
    get: (id: string) => `/municipalityofficialdocumenttypes/${id}`,
    create: '/municipalityofficialdocumenttypes',
    update: (id: string) => `/municipalityofficialdocumenttypes/${id}`,
    delete: (id: string) => `/municipalityofficialdocumenttypes/${id}`,
  },
  municipalityOfficialDocumentTemplates: {
    list: '/municipalityofficialdocumenttemplates',
    get: (id: string) => `/municipalityofficialdocumenttemplates/${id}`,
    create: '/municipalityofficialdocumenttemplates',
    update: (id: string) => `/municipalityofficialdocumenttemplates/${id}`,
    delete: (id: string) => `/municipalityofficialdocumenttemplates/${id}`,
  },
  municipalityOfficialDocumentApprovals: {
    list: '/municipalityofficialdocumentapprovals',
    get: (id: string) => `/municipalityofficialdocumentapprovals/${id}`,
    create: '/municipalityofficialdocumentapprovals',
    update: (id: string) => `/municipalityofficialdocumentapprovals/${id}`,
  },
  municipalityCollection: {
    people: '/municipalitycollection/people',
    penalties: '/municipalitycollection/penalties',
    blacklist: '/municipalitycollection/blacklist',
    clearBlacklist: '/municipalitycollection/clear-blacklist',
  },

  // Municipality Tenders
  tenders: {
    list: (branchId?: string, statusId?: number) => {
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId);
      if (statusId != null) params.append('statusId', statusId.toString());
      const query = params.toString();
      return `/tenders?${query}`;
    },
    get: (id: string) => `/tenders/${id}`,
    create: '/tenders',
    update: (id: string) => `/tenders/${id}`,
    delete: (id: string) => `/tenders/${id}`,
    items: {
      list: (id: string) => `/tenders/${id}/items`,
      add: (id: string) => `/tenders/${id}/items`,
      delete: (id: string, itemId: string) => `/tenders/${id}/items/${itemId}`,
    },
    competitors: {
      list: (id: string) => `/tenders/${id}/competitors`,
      add: (id: string) => `/tenders/${id}/competitors`,
      delete: (id: string, competitorId: string) => `/tenders/${id}/competitors/${competitorId}`,
    },
    expenses: (id: string) => `/tenders/${id}/expenses`,
    analysis: (id: string) => `/tenders/${id}/analysis`,
    cashFlowImpact: (id: string) => `/tenders/${id}/cash-flow-impact`,
    result: (id: string) => `/tenders/${id}/result`,
  },
  municipalityTenders: {
    list: (branchId?: string, statusId?: number) => {
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId);
      if (statusId != null) params.append('statusId', statusId.toString());
      const query = params.toString();
      return `/municipalitytenders${query ? `?${query}` : ''}`;
    },
    get: (id: string) => `/municipalitytenders/${id}`,
    create: '/municipalitytenders',
    update: (id: string) => `/municipalitytenders/${id}`,
    delete: (id: string) => `/municipalitytenders/${id}`,
    items: {
      list: (id: string) => `/municipalitytenders/${id}/items`,
      add: (id: string) => `/municipalitytenders/${id}/items`,
      delete: (id: string, itemId: string) => `/municipalitytenders/${id}/items/${itemId}`,
    },
    competitors: {
      list: (id: string) => `/municipalitytenders/${id}/competitors`,
      add: (id: string) => `/municipalitytenders/${id}/competitors`,
      delete: (id: string, competitorId: string) => `/municipalitytenders/${id}/competitors/${competitorId}`,
    },
    expenses: (id: string) => `/municipalitytenders/${id}/expenses`,
    analysis: (id: string) => `/municipalitytenders/${id}/analysis`,
    cashFlowImpact: (id: string) => `/municipalitytenders/${id}/cash-flow-impact`,
    result: (id: string) => `/municipalitytenders/${id}/result`,
  },

  // Sorting System
  sorting: {
    list: (branchId?: string, status?: string, searchTerm?: string) => {
      const params = new URLSearchParams();
      if (branchId) params.append('branchId', branchId);
      if (status) params.append('status', status);
      if (searchTerm) params.append('searchTerm', searchTerm);
      const query = params.toString();
      return `/sorting${query ? `?${query}` : ''}`;
    },
    get: (id: string) => `/sorting/${id}`,
    create: '/sorting',
    update: (id: string) => `/sorting/${id}`,
    delete: (id: string) => `/sorting/${id}`,
    confirm: (id: string) => `/sorting/${id}/confirm`,
    post: (id: string) => `/sorting/${id}/post`,
    cancel: (id: string) => `/sorting/${id}/cancel`,
  },

  // Subscription module (ISP / service subscriptions) — DB-backed API
  subscriptionModule: {
    dashboard: (companyId: string) =>
      `/subscriptionModule/dashboard?companyId=${encodeURIComponent(companyId)}`,
    packages: (companyId: string) =>
      `/subscriptionModule/packages?companyId=${encodeURIComponent(companyId)}`,
    package: (companyId: string, id: string) =>
      `/subscriptionModule/packages/${encodeURIComponent(id)}?companyId=${encodeURIComponent(companyId)}`,
    subscribers: (companyId: string) =>
      `/subscriptionModule/subscribers?companyId=${encodeURIComponent(companyId)}`,
    subscriber: (companyId: string, id: string) =>
      `/subscriptionModule/subscribers/${encodeURIComponent(id)}?companyId=${encodeURIComponent(companyId)}`,
    contracts: (companyId: string) =>
      `/subscriptionModule/contracts?companyId=${encodeURIComponent(companyId)}`,
    contract: (companyId: string, id: string) =>
      `/subscriptionModule/contracts/${encodeURIComponent(id)}?companyId=${encodeURIComponent(companyId)}`,
    contractFreeze: (companyId: string, id: string) =>
      `/subscriptionModule/contracts/${encodeURIComponent(id)}/freeze?companyId=${encodeURIComponent(companyId)}`,
    contractStatus: (companyId: string, id: string) =>
      `/subscriptionModule/contracts/${encodeURIComponent(id)}/status?companyId=${encodeURIComponent(companyId)}`,
    contractQuickPay: (companyId: string, id: string) =>
      `/subscriptionModule/contracts/${encodeURIComponent(id)}/quick-pay?companyId=${encodeURIComponent(companyId)}`,
    invoices: (companyId: string) =>
      `/subscriptionModule/invoices?companyId=${encodeURIComponent(companyId)}`,
    invoice: (companyId: string, id: string) =>
      `/subscriptionModule/invoices/${encodeURIComponent(id)}?companyId=${encodeURIComponent(companyId)}`,
    invoicePayment: (companyId: string, id: string) =>
      `/subscriptionModule/invoices/${encodeURIComponent(id)}/payment?companyId=${encodeURIComponent(companyId)}`,
    tickets: (companyId: string) =>
      `/subscriptionModule/tickets?companyId=${encodeURIComponent(companyId)}`,
    ticketStatus: (companyId: string, id: string) =>
      `/subscriptionModule/tickets/${encodeURIComponent(id)}/status?companyId=${encodeURIComponent(companyId)}`,
    alertRules: (companyId: string) =>
      `/subscriptionModule/alert-rules?companyId=${encodeURIComponent(companyId)}`,
    alertRule: (companyId: string, id: string) =>
      `/subscriptionModule/alert-rules/${encodeURIComponent(id)}?companyId=${encodeURIComponent(companyId)}`,
    billingPolicy: (companyId: string) =>
      `/subscriptionModule/billing-policy?companyId=${encodeURIComponent(companyId)}`,
    automation: (companyId: string) =>
      `/subscriptionModule/automation?companyId=${encodeURIComponent(companyId)}`,
    audit: (companyId: string) =>
      `/subscriptionModule/audit?companyId=${encodeURIComponent(companyId)}`,
    reportsSummary: (companyId: string) =>
      `/subscriptionModule/reports/summary?companyId=${encodeURIComponent(companyId)}`,
    reportsSubscribers: (companyId: string, status?: string) =>
      `/subscriptionModule/reports/subscribers?companyId=${encodeURIComponent(companyId)}${status ? `&status=${status}` : ''}`,
    markInvoicePaid: (companyId: string, id: string) =>
      `/subscriptionModule/invoices/${encodeURIComponent(id)}/mark-paid?companyId=${encodeURIComponent(companyId)}`,
    invoiceReceiptData: (companyId: string, id: string) =>
      `/subscriptionModule/invoices/${encodeURIComponent(id)}/receipt-data?companyId=${encodeURIComponent(companyId)}`,
  },

  // Warehouse Bin Management
  warehouseBins: {
    list: (storeId?: string) => storeId ? `/warehousebins?storeId=${storeId}` : '/warehousebins',
    get: (id: string) => `/warehousebins/${id}`,
    create: '/warehousebins',
    update: (id: string) => `/warehousebins/${id}`,
    delete: (id: string) => `/warehousebins/${id}`,
    itemLocations: (itemId: string) => `/warehousebins/item-locations/${itemId}`,
    itemLocation: (itemId: string, storeId: string) => `/warehousebins/item-location?itemId=${itemId}&storeId=${storeId}`,
    linkItem: '/warehousebins/link-item',
    unlinkItem: (itemId: string, binId: string) => `/warehousebins/unlink-item?itemId=${itemId}&binId=${binId}`,
  },

  // SQL Query
  sqlQuery: {
    tables: '/sqlquery/tables',
    views: '/sqlquery/views',
    execute: '/sqlquery/execute',
  },

  fuelStation: {
    dashboard: '/fuel-station/dashboard',
    catalog: '/fuel-station/catalog',
    stations: '/fuel-station/stations',
    stationDelete: (id: number) => `/fuel-station/stations/${id}`,
    products: '/fuel-station/products',
    productDelete: (id: number) => `/fuel-station/products/${id}`,
    productPrice: (id: number) => `/fuel-station/products/${id}/price`,
    dailyPrices: '/fuel-station/daily-prices',
    dailyPriceHistory: '/fuel-station/daily-prices/history',
    tanks: '/fuel-station/tanks',
    tankDelete: (id: number) => `/fuel-station/tanks/${id}`,
    tankChangeProduct: (id: number) => `/fuel-station/tanks/${id}/change-product`,
    tankMovements: (id: number) => `/fuel-station/tanks/${id}/movements`,
    tankBalances: '/fuel-station/tanks/balances',
    pumps: '/fuel-station/pumps',
    pumpDelete: (id: number) => `/fuel-station/pumps/${id}`,
    meters: '/fuel-station/meters',
    meterDelete: (id: number) => `/fuel-station/meters/${id}`,
    users: '/fuel-station/users',
    readings: '/fuel-station/readings',
    readingsManual: '/fuel-station/readings/manual',
    readingsOcr: '/fuel-station/readings/ocr',
    readingApprove: (id: number) => `/fuel-station/readings/${id}/approve`,
    readingReject: (id: number) => `/fuel-station/readings/${id}/reject`,
    readingDelete: (id: number) => `/fuel-station/readings/${id}`,
    readingCorrection: (id: number) => `/fuel-station/readings/${id}/corrections`,
    readingEdit: (id: number) => `/fuel-station/readings/${id}`,
    closeReopen: '/fuel-station/close/reopen',
    closeUndo: '/fuel-station/close/undo',
    editLog: '/fuel-station/edit-log',
    meterReset: '/fuel-station/meters/reset',
    meterOpening: '/fuel-station/meters/opening',
    tankOpening: '/fuel-station/tanks/opening',
    meterResets: '/fuel-station/meters/resets',
    meterResetApprove: (id: number) => `/fuel-station/meters/reset/${id}/approve`,
    deliveries: '/fuel-station/deliveries',
    deliveriesOcr: '/fuel-station/deliveries/ocr',
    deliveryReceive: (id: number) => `/fuel-station/deliveries/${id}/receive`,
    deliveryUnreceive: (id: number) => `/fuel-station/deliveries/${id}/unreceive`,
    deliveryInvoice: (id: number) => `/fuel-station/deliveries/${id}/purchase-invoice`,
    deliveryArchive: (id: number, archived = true) => `/fuel-station/deliveries/${id}/archive?archived=${archived ? 'true' : 'false'}`,
    loss: '/fuel-station/loss',
    adjustments: '/fuel-station/adjustments',
    adjustmentPreview: '/fuel-station/adjustments/preview',
    adjustmentSettle: '/fuel-station/adjustments/settle',
    adjustmentApprove: (id: number) => `/fuel-station/adjustments/${id}/approve`,
    closePreview: '/fuel-station/close/preview',
    close: '/fuel-station/close',
    dailySheet: '/fuel-station/daily-sheet',
    dailyLoss: '/fuel-station/daily-loss',
    correctionApprove: (id: number) => `/fuel-station/corrections/${id}/approve`,
    report: (name: string) => `/fuel-station/reports/${name}`,
    cashStatement: '/fuel-station/cash-statement',
    notifications: '/fuel-station/notifications',
    notificationsUnreadCount: '/fuel-station/notifications/unread-count',
    notificationRead: (id: string) => `/fuel-station/notifications/${id}/read`,
    notificationsReadAll: '/fuel-station/notifications/read-all',
    vouchers: '/fuel-station/vouchers',
    voucherLookup: '/fuel-station/vouchers/lookup',
    attendantSettlement: '/fuel-station/attendant-settlement',
    attendantCollections: '/fuel-station/attendant-collections',
    moduleSettings: '/fuel-station/module-settings',
    lowStock: '/fuel-station/low-stock',
    supplyRequests: '/fuel-station/supply-requests',
    supplyLink: (id: number) => `/fuel-station/supply-requests/${id}/link`,
    quotas: '/fuel-station/quotas',
    transfers: '/fuel-station/transfers',
    serviceLogs: '/fuel-station/service-logs',
    bankDeposits: '/fuel-station/bank-deposits',
    bankDepositDelete: (id: number) => `/fuel-station/bank-deposits/${id}`,
    priceApprovals: '/fuel-station/price-approvals',
    priceApprovalsPublish: '/fuel-station/price-approvals/publish',
    priceApprovalReject: (id: number) => `/fuel-station/price-approvals/${id}/reject`,
    shopItems: '/fuel-station/shop/items',
    shopSales: '/fuel-station/shop/sales',
    compare: '/fuel-station/compare',
    officialExport: '/fuel-station/official-export',
    prepaidCoupons: '/fuel-station/prepaid-coupons',
    prepaidCoupon: (id: number) => `/fuel-station/prepaid-coupons/${id}`,
    prepaidByCode: '/fuel-station/prepaid-coupons/by-code',
    prepaidRedeem: '/fuel-station/prepaid-coupons/redeem',
    prepaidCancel: (id: number) => `/fuel-station/prepaid-coupons/${id}/cancel`,
  },

  delivery: {
    dashboard: '/delivery/dashboard',
    lookups: '/delivery/lookups',
    settings: '/delivery/settings',
    zones: '/delivery/zones',
    drivers: '/delivery/drivers',
    vehicles: '/delivery/vehicles',
    slots: '/delivery/slots',
    orders: '/delivery/orders',
    order: (id: number) => `/delivery/orders/${id}`,
    fromInvoice: '/delivery/orders/from-invoice',
    linkInvoice: (id: number) => `/delivery/orders/${id}/link-invoice`,
    status: (id: number) => `/delivery/orders/${id}/status`,
    deliver: (id: number) => `/delivery/orders/${id}/deliver`,
    cod: (id: number) => `/delivery/orders/${id}/cod`,
    assign: (id: number) => `/delivery/orders/${id}/assign`,
    autoAssign: (id: number) => `/delivery/orders/${id}/auto-assign`,
    expenses: '/delivery/expenses',
    settlementPreview: '/delivery/settlements/preview',
    settlements: '/delivery/settlements',
    notifications: '/delivery/notifications',
    reports: '/delivery/reports',
    track: (token: string) => `/delivery/track/${token}`,
  },

  partners: {
    list: '/partners',
    overview: '/partners/overview',
    settings: '/partners/settings',
    updateAccountMapping: '/partners/settings/account-mapping',
    accountSuggestions: '/partners/account-suggestions',
    dashboard: (id: string) => `/partners/${id}/dashboard`,
    statement: (id: string) => `/partners/${id}/statement`,
    drawings: '/partners/drawings',
    expenses: '/partners/expenses',
    loans: '/partners/loans',
    settlements: '/partners/settlements',
    capital: '/partners/capital',
    openingBalances: '/partners/opening-balances',
    cancel: (type: string, id: string) => `/partners/${type}/${id}/cancel`,
  },
  partnerOwnership: {
    list: '/partner-ownership',
    structures: '/partner-ownership/structures',
    createStructure: '/partner-ownership/structures',
    activate: (id: string) => `/partner-ownership/structures/${id}/activate`,
    history: '/partner-ownership/history',
    create: '/partner-ownership',
    update: (id: string) => `/partner-ownership/${id}`,
    cancel: (id: string) => `/partner-ownership/${id}/cancel`,
  },
  profitPeriods: {
    list: '/profit-periods',
    create: '/profit-periods',
    calculate: (id: string) => `/profit-periods/${id}/calculate`,
    adjustments: (id: string) => `/profit-periods/${id}/adjustments`,
    approve: (id: string) => `/profit-periods/${id}/approve`,
    allocate: (id: string) => `/profit-periods/${id}/allocate`,
    close: (id: string) => `/profit-periods/${id}/close`,
    reopen: (id: string) => `/profit-periods/${id}/reopen`,
    allocations: (id: string) => `/profit-periods/${id}/allocations`,
  },
  profitDistributions: {
    list: '/profit-distributions',
    create: '/profit-distributions',
  },
  partnerReports: {
    ownership: '/partner-reports/ownership',
    allocations: '/partner-reports/allocations',
    balances: '/partner-reports/balances',
    capital: '/partner-reports/capital',
    aging: '/partner-reports/aging',
  },
} as const
